import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Calendar, Users, Trophy, Settings, SlidersHorizontal, Plus, Waves, Fish, Anchor, Edit, Eye, Trash2, Grid, List, ChevronDown, Pause, Timer, Clock, Flag, X, FileText, EyeOff, StopCircle, Trash, CalendarDays, MapPin, Clock3, Users2, Pencil, Lock, Globe, LayoutGrid, ChevronRight, Search, LogIn, LogOut, User, Shield, Languages, BarChart3, BookOpen, Layers, Award, Play, Building2, ExternalLink, Briefcase } from 'lucide-react';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Textarea } from './components/ui/textarea';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Alert, AlertDescription } from './components/ui/alert';

// Import tab components
import DashboardTab from './components/tabs/DashboardTab';
import AquaticInfrastructureTab from './components/tabs/AquaticInfrastructureTab';
import FollowUpTab from './components/tabs/FollowUpTab';
import PerformanceComparisonTab from './components/tabs/PerformanceComparisonTab';
import ExerciseLibraryTab from './components/tabs/ExerciseLibraryTab';
import SessionBuilderTab from './components/tabs/SessionBuilderTab';
import CompetitionsTab from './components/tabs/CompetitionsTab';
import FreediversTab from './components/tabs/FreediversTab';
import LiveEventsTab from './components/tabs/LiveEventsTab';
import TrainingSessionsTab from './components/tabs/TrainingSessionsTab';
import ApneaTablesTab from './components/tabs/ApneaTablesTab';
import GlobalSettingsDialog from './components/settings/GlobalSettingsDialog';
import EventResultsTab from './components/tabs/EventResultsTab';
import EventResultsModal from './components/EventResultsModal';
import RegistrationsTab from './components/tabs/RegistrationsTab';
import FreedivingLevelsTab from './components/tabs/FreedivingLevelsTab';
import OrganizationsTab from './components/tabs/OrganizationsTab';
import UserManagementModal from './components/UserManagementModal';

// Import translations
import translations from './utils/translations';

// Import utilities
import { 
  formatTimeDisplay, 
  formatCountdown, 
  formatDateTime, 
  formatDateTimeWithSeconds,
  formatDuration,
  formatDate,
  formatDateShort,
  formatFullDateTime
} from './utils/formatters';
import { 
  calculatePenaltyDisplay, 
  checkEventCompletion, 
  getCountdownPhase,
  getStatusBadgeClass,
  getCardColorClass
} from './utils/helpers';
import { 
  PENALTY_CODES,
  CARD_RESULTS,
  PASSAGE_STATUSES,
  SESSION_STATUSES,
  DEFAULT_TIMINGS
} from './utils/constants';

import './App.css';

// API base URL — must come from REACT_APP_BACKEND_URL env (set in /app/frontend/.env)
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  // Authentication states - declare first as they're used in language callbacks
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken') || '');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [globalSettings, setGlobalSettings] = useState({});
  const [settingsForm, setSettingsForm] = useState({
    duration_per_passage_minutes: 6,
    briefing_duration_minutes: 15,
    warmup_duration_minutes: 45,
    official_zone_duration_minutes: 2,
    duration_before_protest_minutes: 15,
    protest_time_duration_minutes: 15,
    event_live_simulation: false
  });
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');

  // Language state
  const [language, setLanguageState] = useState(() => {
    return localStorage.getItem('acm_language') || 'en';
  });

  // Translation function
  const t = useCallback((key, fallback = '') => {
    const translation = translations[language]?.[key];
    if (translation) return translation;
    const englishTranslation = translations['en']?.[key];
    if (englishTranslation) return englishTranslation;
    return fallback || key;
  }, [language]);

  // RBAC: check if current user has access to a module
  const hasModule = useCallback((moduleId) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    return (currentUser.allowed_modules || []).includes(moduleId);
  }, [currentUser]);

  // RBAC: check if any module in a group is accessible
  const hasAnyModuleInGroup = useCallback((moduleIds) => {
    if (!currentUser) return false;
    if (currentUser.role === 'admin') return true;
    return moduleIds.some(id => (currentUser.allowed_modules || []).includes(id));
  }, [currentUser]);

  // Set language and persist
  const setLanguage = useCallback(async (lang) => {
    setLanguageState(lang);
    localStorage.setItem('acm_language', lang);
    
    // If authenticated, save to user preferences
    if (authToken && currentUser) {
      try {
        await axios.put(`${API_BASE_URL}/api/users/me/language`, 
          { language: lang },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      } catch (error) {
        console.error('Error saving language preference:', error);
      }
    }
  }, [authToken, currentUser]);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [showContestsDropdown, setShowContestsDropdown] = useState(false);
  const [showCareerDropdown, setShowCareerDropdown] = useState(false); // Career dropdown state
  const [showTrainingDropdown, setShowTrainingDropdown] = useState(false); // Training Module dropdown state
  const [showLevelsDropdown, setShowLevelsDropdown] = useState(false); // Freediving Levels dropdown state
  const [showManagementDropdown, setShowManagementDropdown] = useState(false); // Management dropdown state
  const [selectedLevelAgency, setSelectedLevelAgency] = useState(null); // Selected agency for levels view
  const [isActivating, setIsActivating] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [countdownToNext, setCountdownToNext] = useState(null); // Countdown timer
  const [currentPassageIndex, setCurrentPassageIndex] = useState(0);
  const [isPerformanceLocked, setIsPerformanceLocked] = useState(true); // Lock until Official Top
  const [countdownPaused, setCountdownPaused] = useState(false); // Pause countdown state
  const [countdownActive, setCountdownActive] = useState(false); // Whether countdown is running
  const countdownIntervalRef = React.useRef(null);
  // Global event timer
  const [eventStartTime, setEventStartTime] = useState(null);
  const [eventElapsedTime, setEventElapsedTime] = useState(0);
  const eventTimerRef = React.useRef(null);
  const [competitions, setCompetitions] = useState([]);
  const [freedivers, setFreedivers] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  // Event timing states
  const [eventSessions, setEventSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState(null);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [countdownTimer, setCountdownTimer] = useState(null);
  const [realTimeCountdown, setRealTimeCountdown] = useState('');
  const [stopwatchTime, setStopwatchTime] = useState(0);
  const [stopwatchRunning, setStopwatchRunning] = useState(false);
  const [performanceInput, setPerformanceInput] = useState('');
  const [penaltiesInput, setPenaltiesInput] = useState([]);
  const [countdownInterval, setCountdownInterval] = useState(null);
  const [stopwatchInterval, setStopwatchInterval] = useState(null);
  // Running order states
  const [runningOrder, setRunningOrder] = useState(null);
  const [selectedCardResult, setSelectedCardResult] = useState(''); // 'white', 'yellow', 'red'
  const [showRunningOrder, setShowRunningOrder] = useState(false);
  // Active live session banner
  const [activeLiveSession, setActiveLiveSession] = useState(null);
  const activeLivePollingRef = React.useRef(null);
  // Results states
  const [showResults, setShowResults] = useState(false);
  const [eventResults, setEventResults] = useState(null);
  const [resultsGenderFilter, setResultsGenderFilter] = useState('all'); // 'all', 'M', 'F'
  // Next freediver countdown states
  const [nextFreediverCountdown, setNextFreediverCountdown] = useState({ warmup: null, transition: null, official: null });
  const [countdownIntervals, setCountdownIntervals] = useState([]);
  const [showNextFreediverCountdown, setShowNextFreediverCountdown] = useState(false);
  // Enhanced save workflow states
  const [rpSaved, setRpSaved] = useState(false);
  const [savedRpValue, setSavedRpValue] = useState('');
  const [cardSaved, setCardSaved] = useState(false);
  const [isRpEditable, setIsRpEditable] = useState(false);
  // Performance workflow states
  const [performanceStarted, setPerformanceStarted] = useState(false);
  const [performanceStartTime, setPerformanceStartTime] = useState(null);
  const [performanceChronoTime, setPerformanceChronoTime] = useState(0);
  const [performanceChronoRunning, setPerformanceChronoRunning] = useState(false);
  const [showStartPerformanceButton, setShowStartPerformanceButton] = useState(false);
  const [lastSavedResult, setLastSavedResult] = useState(null); // Store last saved performance result
  const performanceChronoRef = useRef(null);
  // Training session states
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [exerciseLibrary, setExerciseLibrary] = useState({});
  const [trainingTemplates, setTrainingTemplates] = useState({});
  const [customTemplates, setCustomTemplates] = useState([]);
  
  // Aquatic Infrastructure states
  const [aquaticInfrastructures, setAquaticInfrastructures] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [showInfrastructureModal, setShowInfrastructureModal] = useState(false);
  const [editingInfrastructure, setEditingInfrastructure] = useState(null);
  const [infrastructureViewMode, setInfrastructureViewMode] = useState('list'); // 'grid' or 'list' - default to list
  const [infrastructureForm, setInfrastructureForm] = useState({
    short_name: '',
    long_name: '',
    pool_type: '25m',
    pool_length: '',
    min_depth: '',
    max_depth: '',
    number_of_lanes: '',
    max_capacity: '',
    logo_url: '',
    website_url: '',
    address_number: '',
    address_street: '',
    address_type: 'Street',
    city: '',
    postal_code: '',
    province: '',
    country: '',
    organization: '',
    building_name: ''
  });
  const [infrastructureLogoPreview, setInfrastructureLogoPreview] = useState(null);
  const infrastructureLogoInputRef = useRef(null);
  
  // Follow Up states
  const [performances, setPerformances] = useState([]);
  const [selectedFreediverForFollowUp, setSelectedFreediverForFollowUp] = useState(null);
  const [freediverPerformances, setFreediverPerformances] = useState([]);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);
  const [editingPerformance, setEditingPerformance] = useState(null);
  const [certificationLevels, setCertificationLevels] = useState([]);
  const [followUpViewMode, setFollowUpViewMode] = useState('grid'); // 'grid' or 'list'
  const [followUpSearchQuery, setFollowUpSearchQuery] = useState(''); // Search query for freedivers
  const [coaches, setCoaches] = useState([]); // List of freedivers with is_coach=true
  const [newPerformance, setNewPerformance] = useState({
    freediver_id: '',
    performance_date: new Date().toISOString().split('T')[0], // Pre-fill with current date
    location: '',
    pool_type: '25m',
    coach_id: '', // Coach freediver ID (those with is_coach=true)
    performance_type: 'DYN',
    distance: '',
    duration_seconds: 0,
    duration_minutes: '',
    duration_secs: '',
    comments: '',
    preparation_protocol: ''
  });
  
  const CERTIFICATION_AGENCIES = [
    "CMAS", "AIDA", "PADI", "Apnea Academy", "PFI", "SSI", "ADIP", "FFESSM", "OTHER"
  ];
  
  const ADDRESS_TYPES = ["Street", "Avenue", "Boulevard", "Road", "Drive", "Lane", "Place", "Way", "Court"];
  
  const PERFORMANCE_TYPES = ["DYN", "DNF", "STA", "DYNBF", "CNF", "CWT", "FIM", "CWTBF", "16x25m", "16x50m"];
  const POOL_TYPES = ["25m", "50m", "Open Water", "Ocean", "Quarry", "Lake", "River", "Indoor Pool", "Outdoor Pool", "Other"];

  // useEffect to handle running order display
  useEffect(() => {
    // Force re-render when both conditions are met
    if (showRunningOrder && runningOrder) {
      console.log('Running order should display:', { showRunningOrder, runningOrder: !!runningOrder });
    }
  }, [showRunningOrder, runningOrder]);

  // Fetch data on component mount — only when authenticated to avoid 401 spam pre-login
  useEffect(() => {
    if (!isAuthenticated || !authToken) return;
    fetchCompetitions();
    fetchFreedivers();
    fetchEventTypes();
    fetchEventSessions();
    fetchTrainingSessions();
    fetchExerciseLibrary();
    fetchTrainingTemplates();
    fetchCustomTemplates();
    fetchRegistrations(); // Add this to fetch registrations on mount
    fetchSettings();
    fetchPerformances(); // Fetch performances for Performance Comparison module
    // Poll active live session every 5 seconds for the top banner
    fetchActiveLiveSession();
    activeLivePollingRef.current = setInterval(fetchActiveLiveSession, 5000);
    return () => {
      if (activeLivePollingRef.current) clearInterval(activeLivePollingRef.current);
    };
  }, [isAuthenticated, authToken]);

  // Auto-sync currentSession/currentStatus when an active live session is detected
  useEffect(() => {
    const syncActiveSession = async () => {
      if (activeLiveSession && activeLiveSession.id && currentSession !== activeLiveSession.id) {
        setCurrentSession(activeLiveSession.id);
        setEventStartTime(activeLiveSession.actual_start_time || activeLiveSession.event_start_time);
        
        try {
          const statusRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${activeLiveSession.id}/current-status`);
          setCurrentStatus(statusRes.data);
        } catch (e) {
          console.log('Auto-sync status error:', e);
        }
        
        try {
          const roRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${activeLiveSession.id}/running-order`);
          setRunningOrder(roRes.data);
          
          // Use server-calculated seconds_to_official_top (avoids timezone issues)
          const passages = roRes.data.running_order || [];
          const nextPassage = passages.find(p => !p.card_result && p.status !== 'completed') || passages[0];
          if (nextPassage && nextPassage.seconds_to_official_top !== undefined) {
            const secondsUntilTop = nextPassage.seconds_to_official_top;
            if (secondsUntilTop > 0 && secondsUntilTop < 86400) {
              setCountdownToNext(secondsUntilTop);
              setCountdownActive(true);
            }
          }
        } catch (e) {
          console.log('Auto-sync running order error:', e);
        }
      } else if (!activeLiveSession && currentSession) {
        // No active session anymore - clear state
        setCurrentSession(null);
        setCurrentStatus(null);
        setEventStartTime(null);
        setCountdownToNext(null);
        setCountdownActive(false);
        setRunningOrder(null);
      }
    };
    syncActiveSession();
  }, [activeLiveSession]);



  // Countdown effect - handles the actual countdown timer
  useEffect(() => {
    // Only start interval if countdown is active and not paused
    if (countdownActive && !countdownPaused && countdownToNext !== null) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdownToNext(prev => {
          if (prev === null || prev <= 0) {
            // Countdown reached 0, unlock performance entry
            setIsPerformanceLocked(false);
            setCountdownActive(false);
            setShowStartPerformanceButton(true); // Show start performance button at Official Top
            setRealTimeCountdown('OFFICIAL TOP');
            
            return 0;
          }
          
          const newValue = prev - 1;
          
          // Auto-start the official countdown phase 3 minutes (180 seconds) before Official Top
          if (newValue === 180) {
            setRealTimeCountdown('3:00');
          }
          
          // Update real-time countdown display for the last 3 minutes
          if (newValue <= 180 && newValue > 0) {
            const mins = Math.floor(newValue / 60);
            const secs = newValue % 60;
            setRealTimeCountdown(`${mins}:${secs.toString().padStart(2, '0')}`);
          }
          
          // Show Start Performance button 10 seconds before Official Top
          if (newValue <= 10 && newValue > 0) {
            setShowStartPerformanceButton(true);
            setIsPerformanceLocked(false);
          }
          
          return newValue;
        });
      }, 1000);
    } else if (countdownPaused && countdownIntervalRef.current) {
      // Pause: Clear the interval but keep the countdown value
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    // Cleanup function
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [countdownActive, countdownPaused, countdownToNext]);

  // Global event timer - counts UP from event start; freezes when session is paused
  useEffect(() => {
    if (eventStartTime) {
      eventTimerRef.current = setInterval(() => {
        if (currentStatus?.session?.is_paused) return; // freeze while paused
        const totalPausedSec = currentStatus?.session?.total_paused_seconds || 0;
        const elapsed = Math.floor((Date.now() - new Date(eventStartTime).getTime()) / 1000) - totalPausedSec;
        setEventElapsedTime(Math.max(0, elapsed));
      }, 1000);
    } else {
      if (eventTimerRef.current) {
        clearInterval(eventTimerRef.current);
        eventTimerRef.current = null;
      }
    }
    
    return () => {
      if (eventTimerRef.current) {
        clearInterval(eventTimerRef.current);
      }
    };
  }, [eventStartTime, currentStatus?.session?.is_paused, currentStatus?.session?.total_paused_seconds]);

  // Performance chrono effect - counts up during performance
  useEffect(() => {
    if (performanceChronoRunning) {
      performanceChronoRef.current = setInterval(() => {
        setPerformanceChronoTime(prev => prev + 0.1);
      }, 100);
    } else {
      if (performanceChronoRef.current) {
        clearInterval(performanceChronoRef.current);
        performanceChronoRef.current = null;
      }
    }
    
    return () => {
      if (performanceChronoRef.current) {
        clearInterval(performanceChronoRef.current);
      }
    };
  }, [performanceChronoRunning]);

  // Start Performance function
  const startPerformance = () => {
    setPerformanceStarted(true);
    setPerformanceStartTime(new Date());
    setPerformanceChronoTime(0);
    setPerformanceChronoRunning(true);
    setShowStartPerformanceButton(false);
    // Disable RP field and penalties until performance ends
    setIsRpEditable(false);
    setRpSaved(false);
    setSavedRpValue('');
    setPerformanceInput('');
    setPenaltiesInput([]);
    setSelectedCardResult('');
  };

  // End Performance function
  const endPerformance = () => {
    setPerformanceChronoRunning(false);
    setPerformanceStarted(false);
    // Enable RP field and penalties for entry
    setIsRpEditable(true);
    // Store the duration
    const duration = Math.floor(performanceChronoTime);
    setStopwatchTime(duration);
  };

  // Save RP and complete performance
  const savePerformanceResult = async () => {
    if (!currentStatus?.current_passage) return;
    
    const passage = currentStatus.current_passage;
    const freediver = passage.registration?.freediver;
    const duration = Math.floor(performanceChronoTime);
    
    // Prepare result summary
    const result = {
      freediver_name: `${freediver?.first_name || ''} ${freediver?.last_name || ''}`,
      official_top: passage.official_top_time,
      announced_performance: passage.registration?.announced_performance || 0,
      realized_performance: parseFloat(performanceInput) || 0,
      duration_seconds: duration,
      penalties: [...penaltiesInput],
      card_result: selectedCardResult,
      saved_at: new Date().toISOString()
    };
    
    // Save to backend
    try {
      await updatePerformanceWithCard(
        passage.id,
        performanceInput,
        duration,
        selectedCardResult,
        penaltiesInput,
        `Performance saved for ${result.freediver_name}`
      );
      
      // Store the last saved result for display
      setLastSavedResult(result);
      
      // Reset for next contestant
      setRpSaved(true);
      setIsRpEditable(false);
      setShowStartPerformanceButton(false);
      setPerformanceChronoTime(0);
      
    } catch (error) {
      console.error('Error saving performance:', error);
      alert('Error saving performance. Please try again.');
    }
  };

  // Modal states
  const [showCompetitionModal, setShowCompetitionModal] = useState(false);
  const [showEditCompetitionModal, setShowEditCompetitionModal] = useState(false);
  const [showFreediverModal, setShowFreediverModal] = useState(false);
  const [showEditFreediverModal, setShowEditFreediverModal] = useState(false);
  const [showScoringConfigModal, setShowScoringConfigModal] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [selectedFreediver, setSelectedFreediver] = useState(null);
  const [competitionRegistrations, setCompetitionRegistrations] = useState({});
  const [competitionsViewMode, setCompetitionsViewMode] = useState('grid'); // 'grid' or 'list'
  const [freediversViewMode, setFreediversViewMode] = useState('list'); // 'grid' or 'list' - default to list
  const [freediversNationalityFilter, setFreediversNationalityFilter] = useState('all'); // Nationality filter
  const [freediversGenderFilter, setFreediversGenderFilter] = useState('all'); // Gender filter
  const [freediversSearchQuery, setFreediversSearchQuery] = useState(''); // Search query
  const [expandedCompetitions, setExpandedCompetitions] = useState(new Set());
  const [registrationLockStatus, setRegistrationLockStatus] = useState({}); // {competitionId: {is_locked, reason}}
  
  // Scoring configuration state
  const [scoringConfig, setScoringConfig] = useState({
    depth_points_per_meter: 1.0,
    horizontal_points_per_meter: 0.5,
    static_points_per_second: 0.2,
    late_penalty_per_5s: 1.0,
    early_penalty_per_5s: 1.0,
    turn_penalty: 5.0,
    surface_penalty: 5.0,
    grab_penalty: 5.0,
    underperformance_uses_points: true
  });

  // Fetch registration lock status for a competition
  const fetchRegistrationLockStatus = async (competitionId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/competitions/${competitionId}/registration-status`);
      setRegistrationLockStatus(prev => ({
        ...prev,
        [competitionId]: response.data
      }));
      return response.data;
    } catch (error) {
      console.error('Error fetching registration lock status:', error);
      return { is_locked: false };
    }
  };

  // Fetch scoring configuration for a competition
  const fetchScoringConfig = async (competitionId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/competitions/${competitionId}/scoring-config`);
      setScoringConfig(response.data.scoring_config);
      return response.data.scoring_config;
    } catch (error) {
      console.error('Error fetching scoring config:', error);
      return null;
    }
  };

  // Open scoring config modal for a competition
  const openScoringConfigModal = async (competition) => {
    setSelectedCompetition(competition);
    await fetchScoringConfig(competition.id);
    setShowScoringConfigModal(true);
  };

  // Save scoring configuration
  const saveScoringConfig = async () => {
    if (!selectedCompetition) return;
    
    try {
      await axios.put(
        `${API_BASE_URL}/api/competitions/${selectedCompetition.id}/scoring-config`,
        scoringConfig
      );
      alert('Scoring configuration saved successfully!');
      setShowScoringConfigModal(false);
    } catch (error) {
      console.error('Error saving scoring config:', error);
      alert(`Error saving scoring configuration: ${error.response?.data?.detail || error.message}`);
    }
  };

  // Compute grouped registrations
  const groupedRegistrations = registrations.reduce((acc, registration) => {
    const eventCode = registration.event_type_code;
    const compId = registration.competition_id;
    const key = `${compId}_${eventCode}`; // Create unique key with competition

    if (!acc[key]) {
      const eventType = eventTypes.find(et => et.code === eventCode);
      const competition = competitions.find(c => c.id === compId);
      // Find any event_session matching this competition + event_type
      const matchingSession = (eventSessions || []).find(s => s.competition_id === compId && s.event_type_code === eventCode);
      const sessionStatus = (matchingSession?.status || '').toLowerCase();
      const competitionStatus = (competition?.status || '').toLowerCase();
      // Hide groups whose competition or event_session is closed/completed/cancelled
      const isHidden = ['completed', 'closed', 'cancelled'].includes(sessionStatus)
        || ['completed', 'closed', 'cancelled'].includes(competitionStatus);
      acc[key] = {
        event_type: eventType || { name: eventCode, description: '', scoring_rule: '', unit: '' },
        competition: competition || { name: 'Unknown Competition', id: compId },
        competition_id: compId,
        event_type_code: eventCode,
        event_session: matchingSession || null,
        session_status: sessionStatus,
        is_hidden: isHidden,
        registrations: []
      };
    }
    acc[key].registrations.push(registration);
    return acc;
  }, {});

  // Filter out hidden groups (closed/completed/cancelled events)
  const visibleGroupedRegistrations = Object.fromEntries(
    Object.entries(groupedRegistrations).filter(([, g]) => !g.is_hidden)
  );

  // Form states
  const [competitionForm, setCompetitionForm] = useState({
    name: '',
    location: '',
    start_date: '',
    end_date: '',
    pool_type: '',
    lanes: '',
    pool_length: '',
    pool_width: '',
    description: '',
    federation: 'Other',
    max_freedivers: '',
    organizer: '',
    contact_name: '',
    contact_email: '',
    contact_phone: '',
    contact_address: '',
    logo_url: '',
    timezone: 'UTC'
  });

  // Common timezones for competition events
  const TIMEZONES = [
    { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
    { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
    { value: 'Europe/Rome', label: 'Europe/Rome (CET/CEST)' },
    { value: 'Europe/Madrid', label: 'Europe/Madrid (CET/CEST)' },
    { value: 'Europe/Athens', label: 'Europe/Athens (EET/EEST)' },
    { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
    { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
    { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
    { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
    { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST/NZDT)' },
    { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
    { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
    { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
    { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT)' },
    { value: 'America/Mexico_City', label: 'America/Mexico_City (CST/CDT)' },
    { value: 'Africa/Cairo', label: 'Africa/Cairo (EET)' },
    { value: 'Africa/Johannesburg', label: 'Africa/Johannesburg (SAST)' },
  ];

  // Logo preview state for edit modal
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);
  
  // Freediver photo upload states
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);
  
  // Medical certificate upload states
  const [certificatePreview, setCertificatePreview] = useState(null);
  const certificateInputRef = useRef(null);
  
  const [freediverForm, setFreediverForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    nationality: '',
    gender: '',
    date_of_birth: '',
    place_of_birth: '',
    photo_url: '',
    certification_agency: '',
    certification_level: '',
    experience_level: '',
    is_coach: false,
    medical_certificate_valid: false,
    medical_certificate_url: '',
    medical_certificate_upload_date: null
  });

  // Check for existing auth token on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      verifyToken(token);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !authToken) return;
    fetchData();
  }, [isAuthenticated, authToken]);

  useEffect(() => {
    if (!isAuthenticated || !authToken) return;
    if (activeTab === 'live-events') {
      fetchEventSessions();
    }
  }, [activeTab, isAuthenticated, authToken]);

  // Cleanup intervals on unmount
  useEffect(() => {
    return () => {
      if (countdownInterval) {
        clearInterval(countdownInterval);
      }
      if (stopwatchInterval) {
        clearInterval(stopwatchInterval);
      }
    };
  }, [countdownInterval, stopwatchInterval]);

  // Authentication functions
  const verifyToken = async (token) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentUser(response.data);
      setIsAuthenticated(true);
      setAuthToken(token);
    } catch (error) {
      console.error('Token verification failed:', error);
      localStorage.removeItem('authToken');
      setAuthToken('');
      setIsAuthenticated(false);
      setCurrentUser(null);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/login`, loginForm);
      const { token, user } = response.data;
      localStorage.setItem('authToken', token);
      setAuthToken(token);
      setCurrentUser(user);
      setIsAuthenticated(true);
      setShowLoginModal(false);
      setLoginForm({ username: '', password: '' });
      // Set language from user preferences
      if (user.language) {
        setLanguageState(user.language);
        localStorage.setItem('acm_language', user.language);
      }
      fetchData(); // Refresh data after login
    } catch (error) {
      console.error('Login failed:', error);
      setLoginError(error.response?.data?.detail || t('invalidCredentials'));
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('authToken');
    setAuthToken('');
    setCurrentUser(null);
    setIsAuthenticated(false);
    setActiveTab('dashboard');
  };


  const fetchData = async () => {
    setLoading(true);
    try {
      const [competitionsRes, freediversRes, eventTypesRes, statsRes, coachesRes, infrastructuresRes, orgsRes, usersRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/competitions`),
        axios.get(`${API_BASE_URL}/api/freedivers`),
        axios.get(`${API_BASE_URL}/api/event-types`),
        axios.get(`${API_BASE_URL}/api/dashboard/stats`),
        axios.get(`${API_BASE_URL}/api/freedivers/coaches`),
        axios.get(`${API_BASE_URL}/api/aquatic-infrastructure`),
        axios.get(`${API_BASE_URL}/api/organizations`).catch(() => ({ data: { organizations: [] } })),
        axios.get(`${API_BASE_URL}/api/users`).catch(() => ({ data: { users: [] } }))
      ]);

      setCompetitions(competitionsRes.data.competitions || []);
      setFreedivers(freediversRes.data.freedivers || []);
      setEventTypes(eventTypesRes.data.event_types || []);
      setStats(statsRes.data.stats || statsRes.data || {});
      setCoaches(coachesRes.data.coaches || []);
      setAquaticInfrastructures(infrastructuresRes.data.infrastructures || infrastructuresRes.data.infrastructure || []);
      setOrganizations(orgsRes.data.organizations || []);
      setAllUsers(usersRes.data.users || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async (competitionId = null) => {
    try {
      const url = competitionId 
        ? `${API_BASE_URL}/api/competitions/${competitionId}/registrations`
        : `${API_BASE_URL}/api/registrations`;
      const response = await axios.get(url);
      setRegistrations(response.data.registrations || []);
    } catch (error) {
      console.error('Error fetching registrations:', error);
    }
  };

  const handleCreateCompetition = async (e) => {
    e.preventDefault();
    try {
      const competitionData = {
        ...competitionForm,
        lanes: parseInt(competitionForm.lanes),
        pool_length: competitionForm.pool_length ? parseInt(competitionForm.pool_length) : null,
        pool_width: competitionForm.pool_width ? parseInt(competitionForm.pool_width) : null,
        max_freedivers: competitionForm.max_freedivers ? parseInt(competitionForm.max_freedivers) : null,
        start_date: new Date(competitionForm.start_date).toISOString(),
        end_date: new Date(competitionForm.end_date).toISOString()
      };

      await axios.post(`${API_BASE_URL}/api/competitions`, competitionData);
      setShowCompetitionModal(false);
      setCompetitionForm({
        name: '', location: '', start_date: '', end_date: '', pool_type: '',
        lanes: '', pool_length: '', pool_width: '', description: '',
        federation: 'Other', max_freedivers: '', organizer: '',
        contact_name: '', contact_email: '', contact_phone: '', contact_address: ''
      });
      fetchData();
    } catch (error) {
      console.error('Error creating competition:', error);
      if (error.response?.status === 400) {
        alert(error.response.data.detail);
      } else {
        alert('Error creating competition. Please try again.');
      }
    }
  };

  const handleCreateFreediver = async (e) => {
    e.preventDefault();
    try {
      const freediverData = {
        ...freediverForm,
        date_of_birth: new Date(freediverForm.date_of_birth).toISOString()
      };

      await axios.post(`${API_BASE_URL}/api/freedivers`, freediverData);
      setShowFreediverModal(false);
      setFreediverForm({
        first_name: '', last_name: '', email: '', nationality: '',
        gender: '', date_of_birth: '', place_of_birth: '', photo_url: '',
        certification_agency: '', certification_level: '', experience_level: '',
        is_coach: false,
        medical_certificate_valid: false,
        medical_certificate_url: '',
        medical_certificate_upload_date: null
      });
      setPhotoPreview(null);
      setCertificatePreview(null);
      fetchData();
    } catch (error) {
      console.error('Error creating freediver:', error);
      if (error.response?.status === 400) {
        alert(error.response.data.detail);
      } else {
        alert('Error registering freediver. Please try again.');
      }
    }
  };

  const handleUpdateFreediver = async (e) => {
    e.preventDefault();
    try {
      const freediverData = {
        ...freediverForm,
        date_of_birth: new Date(freediverForm.date_of_birth).toISOString()
      };

      await axios.put(`${API_BASE_URL}/api/freedivers/${selectedFreediver.id}`, freediverData);
      setShowEditFreediverModal(false);
      setSelectedFreediver(null);
      setFreediverForm({
        first_name: '', last_name: '', email: '', nationality: '',
        gender: '', date_of_birth: '', place_of_birth: '', photo_url: '',
        certification_agency: '', certification_level: '', experience_level: '',
        is_coach: false,
        medical_certificate_valid: false,
        medical_certificate_url: '',
        medical_certificate_upload_date: null
      });
      setPhotoPreview(null);
      setCertificatePreview(null);
      fetchData();
    } catch (error) {
      console.error('Error updating freediver:', error);
      alert('Error updating freediver. Please check if email is already registered by another freediver.');
    }
  };

  const openEditFreediverModal = (freediver) => {
    setSelectedFreediver(freediver);
    setFreediverForm({
      first_name: freediver.first_name,
      last_name: freediver.last_name,
      email: freediver.email,
      nationality: freediver.nationality,
      gender: freediver.gender,
      date_of_birth: freediver.date_of_birth.split('T')[0], // Convert to date input format
      place_of_birth: freediver.place_of_birth || '',
      photo_url: freediver.photo_url || '',
      certification_agency: freediver.certification_agency || '',
      certification_level: freediver.certification_level || '',
      experience_level: freediver.experience_level || '',
      is_coach: freediver.is_coach || false,
      medical_certificate_valid: freediver.medical_certificate_valid || false,
      medical_certificate_url: freediver.medical_certificate_url || '',
      medical_certificate_upload_date: freediver.medical_certificate_upload_date || null
    });
    setPhotoPreview(freediver.photo_url || null);
    setCertificatePreview(freediver.medical_certificate_url || null);
    if (freediver.certification_agency) {
      fetchCertificationLevels(freediver.certification_agency);
    }
    setShowEditFreediverModal(true);
  };

  const fetchCompetitionRegistrations = async (competitionId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/competitions/${competitionId}/registrations/by-event`);
      setCompetitionRegistrations(prev => ({
        ...prev,
        [competitionId]: response.data.events_with_registrations || {}
      }));
    } catch (error) {
      console.error('Error fetching competition registrations:', error);
    }
  };

  const handleUpdateCompetition = async (e) => {
    e.preventDefault();
    try {
      const competitionData = {
        ...competitionForm,
        lanes: parseInt(competitionForm.lanes),
        pool_length: competitionForm.pool_length ? parseInt(competitionForm.pool_length) : null,
        pool_width: competitionForm.pool_width ? parseInt(competitionForm.pool_width) : null,
        max_freedivers: competitionForm.max_freedivers ? parseInt(competitionForm.max_freedivers) : null,
        start_date: new Date(competitionForm.start_date).toISOString(),
        end_date: new Date(competitionForm.end_date).toISOString(),
        logo_url: competitionForm.logo_url || null,
        timezone: competitionForm.timezone || 'UTC'
      };

      await axios.put(`${API_BASE_URL}/api/competitions/${selectedCompetition.id}`, competitionData);
      setShowEditCompetitionModal(false);
      setSelectedCompetition(null);
      setLogoPreview(null);
      setCompetitionForm({
        name: '', location: '', start_date: '', end_date: '', pool_type: '',
        lanes: '', pool_length: '', pool_width: '', description: '',
        federation: 'Other', max_freedivers: '', organizer: '',
        contact_name: '', contact_email: '', contact_phone: '', contact_address: '',
        logo_url: '', timezone: 'UTC'
      });
      fetchData();
    } catch (error) {
      console.error('Error updating competition:', error);
      alert('Error updating competition. Please try again.');
    }
  };

  const openEditCompetitionModal = (competition) => {
    setSelectedCompetition(competition);
    setCompetitionForm({
      name: competition.name,
      location: competition.location,
      start_date: competition.start_date.split('T')[0] + 'T' + competition.start_date.split('T')[1].substring(0, 5),
      end_date: competition.end_date.split('T')[0] + 'T' + competition.end_date.split('T')[1].substring(0, 5),
      pool_type: competition.pool_type,
      lanes: competition.lanes.toString(),
      pool_length: competition.pool_length ? competition.pool_length.toString() : '',
      pool_width: competition.pool_width ? competition.pool_width.toString() : '',
      description: competition.description || '',
      federation: competition.federation || 'Other',
      max_freedivers: competition.max_freedivers ? competition.max_freedivers.toString() : '',
      organizer: competition.organizer || '',
      contact_name: competition.contact_name || '',
      contact_email: competition.contact_email || '',
      contact_phone: competition.contact_phone || '',
      contact_address: competition.contact_address || '',
      logo_url: competition.logo_url || '',
      timezone: competition.timezone || 'UTC'
    });
    // Set logo preview if competition has a logo
    setLogoPreview(competition.logo_url || null);
    setShowEditCompetitionModal(true);
  };

  // Handle logo file selection
  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.match(/^image\/(jpeg|png)$/)) {
        alert('Please select a JPEG or PNG image file.');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image file size must be less than 5MB.');
        return;
      }
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setCompetitionForm({...competitionForm, logo_url: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear logo
  const clearLogo = () => {
    setLogoPreview(null);
    setCompetitionForm({...competitionForm, logo_url: ''});
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  // Handle freediver photo upload
  const handlePhotoUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Photo file size must be less than 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setFreediverForm({...freediverForm, photo_url: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear freediver photo
  const clearPhoto = () => {
    setPhotoPreview(null);
    setFreediverForm({...freediverForm, photo_url: ''});
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  // Handle medical certificate upload
  const handleCertificateUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type (PDF only)
      if (file.type !== 'application/pdf') {
        alert(t('pdfFilesOnly'));
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(t('pdfFilesOnly'));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setCertificatePreview(reader.result);
        setFreediverForm({
          ...freediverForm, 
          medical_certificate_url: reader.result,
          medical_certificate_upload_date: new Date().toISOString()
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // Clear medical certificate
  const clearCertificate = () => {
    setCertificatePreview(null);
    setFreediverForm({
      ...freediverForm, 
      medical_certificate_url: '',
      medical_certificate_upload_date: null
    });
    if (certificateInputRef.current) {
      certificateInputRef.current.value = '';
    }
  };

  // Unlock registrations for a competition (double-click handler)
  const unlockRegistrations = async (competitionId) => {
    if (!window.confirm('Are you sure you want to unlock registrations for this competition? This will allow new registrations even if events have started.')) {
      return;
    }
    // Simply remove the lock status from frontend state
    setRegistrationLockStatus(prev => {
      const newStatus = {...prev};
      delete newStatus[competitionId];
      return newStatus;
    });
    alert('Registrations have been temporarily unlocked for this session.');
  };

  const handleDeleteCompetition = async (competitionId) => {
    if (!window.confirm('Are you sure you want to delete this competition? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/competitions/${competitionId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting competition:', error);
      if (error.response?.status === 400) {
        alert('Cannot delete competition with existing registrations. Please remove all registrations first.');
      } else {
        alert('Error deleting competition. Please try again.');
      }
    }
  };

  const toggleCompetitionExpansion = async (competitionId) => {
    const newExpanded = new Set(expandedCompetitions);
    if (newExpanded.has(competitionId)) {
      newExpanded.delete(competitionId);
    } else {
      newExpanded.add(competitionId);
      // Fetch registrations if not already loaded
      if (!competitionRegistrations[competitionId]) {
        await fetchCompetitionRegistrations(competitionId);
      }
      // Fetch registration lock status
      if (!registrationLockStatus[competitionId]) {
        await fetchRegistrationLockStatus(competitionId);
      }
    }
    setExpandedCompetitions(newExpanded);
  };

  const handleDeleteFreediver = async (freediverId) => {
    if (!window.confirm('Are you sure you want to delete this freediver? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/freedivers/${freediverId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting freediver:', error);
      if (error.response?.status === 400) {
        alert('Cannot delete freediver with existing event registrations. Please unregister from all events first.');
      } else {
        alert('Error deleting freediver. Please try again.');
      }
    }
  };

  // Data fetching functions
  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/competitions`);
      setCompetitions(response.data.competitions || []);
    } catch (error) {
      console.error('Error fetching competitions:', error);
    }
  };

  const fetchFreedivers = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/freedivers`);
      setFreedivers(response.data.freedivers || []);
    } catch (error) {
      console.error('Error fetching freedivers:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/settings`);
      const ep = response.data?.event_parameters || {};
      setGlobalSettings(response.data || {});
      setSettingsForm({
        duration_per_passage_minutes: ep.duration_per_passage_minutes ?? 6,
        briefing_duration_minutes: ep.briefing_duration_minutes ?? 15,
        warmup_duration_minutes: ep.warmup_duration_minutes ?? 45,
        official_zone_duration_minutes: ep.official_zone_duration_minutes ?? 2,
        duration_before_protest_minutes: ep.duration_before_protest_minutes ?? 15,
        protest_time_duration_minutes: ep.protest_time_duration_minutes ?? 15,
        event_live_simulation: ep.event_live_simulation ?? false,
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSettingsSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/api/settings`, {
        event_parameters: {
          duration_per_passage_minutes: parseInt(settingsForm.duration_per_passage_minutes),
          briefing_duration_minutes: parseInt(settingsForm.briefing_duration_minutes),
          warmup_duration_minutes: parseInt(settingsForm.warmup_duration_minutes),
          official_zone_duration_minutes: parseInt(settingsForm.official_zone_duration_minutes),
          duration_before_protest_minutes: parseInt(settingsForm.duration_before_protest_minutes),
          protest_time_duration_minutes: parseInt(settingsForm.protest_time_duration_minutes),
          event_live_simulation: !!settingsForm.event_live_simulation,
        }
      });
      await fetchSettings();
      setShowSettingsModal(false);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSettingsSaving(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/event-types`);
      setEventTypes(response.data.event_types || []);
    } catch (error) {
      console.error('Error fetching event types:', error);
    }
  };

  // Event session management functions
  const fetchActiveLiveSession = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/event-sessions/active-live`);
      setActiveLiveSession(response.data.active_session || null);
    } catch (error) {
      console.error('Error fetching active live session:', error);
    }
  };

  const clearOrphanSessions = async () => {
    if (!confirm(language === 'fr' 
      ? 'Supprimer les sessions orphelines et reinitialiser les sessions bloquees?' 
      : 'Clear orphan sessions and reset stale sessions?')) return;
    try {
      const response = await axios.delete(`${API_BASE_URL}/api/event-sessions/orphans`);
      const { deleted_orphans, reset_stale_sessions } = response.data;
      alert(language === 'fr'
        ? `${deleted_orphans} session(s) orpheline(s) supprimee(s), ${reset_stale_sessions} session(s) reinitialised(s).`
        : `${deleted_orphans} orphan(s) deleted, ${reset_stale_sessions} stale session(s) reset.`);
      fetchEventSessions();
      fetchActiveLiveSession();
    } catch (error) {
      console.error('Error clearing orphan sessions:', error);
      alert('Error clearing orphan sessions.');
    }
  };

  const fetchEventSessions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/event-sessions`);
      const sessions = response.data.sessions || [];
      setEventSessions(sessions);
      
      // Clear the monitor view if no active sessions exist
      const activeSessions = sessions.filter(s => s.status === 'active' || s.status === 'preparation');
      if (activeSessions.length === 0) {
        setCurrentSession(null);
        setCurrentStatus(null);
        setEventStartTime(null);
        setEventElapsedTime(0);
        setCountdownToNext(null);
        setRunningOrder(null);
        setShowRunningOrder(false);
      }
    } catch (error) {
      console.error('Error fetching event sessions:', error);
    }
  };

  const fetchEventSessionsForResults = async (competitionId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/event-sessions`);
      const filtered = response.data.sessions.filter(s => s.competition_id === competitionId);
      setEventSessions(filtered);
    } catch (error) {
      console.error('Error fetching event sessions for results:', error);
    }
  };

  const createEventSession = async (competitionId, eventTypeCode, intervalMinutes = 6) => {
    try {
      const sessionData = {
        competition_id: competitionId,
        event_type_code: eventTypeCode,
        name: `${eventTypeCode} - Live Session`,
        start_time: new Date().toISOString(),
        preparation_duration: 45,
        interval_duration: intervalMinutes
      };

      await axios.post(`${API_BASE_URL}/api/event-sessions`, sessionData);
      fetchEventSessions();
      alert('Event session created successfully!');
    } catch (error) {
      console.error('Error creating event session:', error);
      alert('Error creating event session. Please ensure there are registrations for this event.');
    }
  };

  const startEventSession = async (sessionId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/event-sessions/${sessionId}/start`);
      fetchEventSessions();
      alert('Event session started! Preparation period has begun.');
    } catch (error) {
      console.error('Error starting event session:', error);
      alert('Error starting event session.');
    }
  };

  const activateEventSession = async (sessionId) => {
    if (isActivating) return; // Prevent double-click
    setIsActivating(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/event-sessions/${sessionId}/activate`);
      console.log('Activate response:', response.data);
      alert('Event activated successfully!');
      await fetchEventSessions(); // Wait for refresh
      setCurrentSession(sessionId);
      startStatusPolling(sessionId);
    } catch (error) {
      console.error('Error activating event session:', error);
      alert(`Error activating event session: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsActivating(false);
    }
  };

  const startStatusPolling = (sessionId) => {
    // Poll for real-time updates every 1 second
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/event-sessions/${sessionId}/current-status`);
        setCurrentStatus(response.data);
      } catch (error) {
        console.error('Error fetching current status:', error);
      }
    }, 1000);

    return interval;
  };

  // Initialize countdown timer from running order when Monitor Live is clicked
  const initializeCountdownFromRunningOrder = async (sessionId) => {
    try {
      // Fetch running order data
      const response = await axios.get(`${API_BASE_URL}/api/event-sessions/${sessionId}/running-order`);
      const runningOrderData = response.data;
      
      if (!runningOrderData || !runningOrderData.running_order || runningOrderData.running_order.length === 0) {
        return;
      }
      
      // Find the current passage (first one without completed performance or the first one)
      const passages = runningOrderData.running_order;
      let currentPassage = passages.find(p => 
        !p.card_result || p.status === 'countdown' || p.status === 'performing' || p.status === 'waiting'
      );
      
      // If no incomplete passage found, use the first one
      if (!currentPassage) {
        currentPassage = passages[0];
      }
      
      // Use server-calculated seconds_to_official_top (avoids timezone issues)
      if (currentPassage && currentPassage.seconds_to_official_top !== undefined) {
        const secondsUntilTop = currentPassage.seconds_to_official_top;
        
        if (secondsUntilTop > 0) {
          setCountdownToNext(secondsUntilTop);
          setCountdownActive(true);
          setCountdownPaused(false);
          setIsPerformanceLocked(true);
          setCurrentPassageIndex(passages.indexOf(currentPassage));
        } else {
          // Official Top has passed - unlock performance entry
          setCountdownToNext(0);
          setIsPerformanceLocked(false);
          setCountdownActive(false);
        }
      }
      
      // Store running order in currentStatus for later reference
      setCurrentStatus(prev => ({
        ...prev,
        running_order: passages
      }));
      
    } catch (error) {
      console.error('Error initializing countdown from running order:', error);
    }
  };

  const updatePerformance = async (passageId, distance, penalties = []) => {
    try {
      const performanceData = {
        distance_covered: parseFloat(distance),
        penalties: penalties,
        notes: ''
      };

      await axios.post(`${API_BASE_URL}/api/passages/${passageId}/performance`, performanceData);
      // Status will be updated in the next polling cycle
    } catch (error) {
      console.error('Error updating performance:', error);
      alert('Error updating performance.');
    }
  };

  const completePassage = async (passageId) => {
    // Validate that all required fields are filled before completing
    if (!performanceInput || performanceInput === '') {
      alert('Please enter the Realized Performance (RP) before completing.');
      return;
    }
    if (!selectedCardResult) {
      alert('Please select a card result (WHITE, YELLOW, or RED) before completing.');
      return;
    }
    if (performanceChronoTime === 0 && stopwatchTime === 0) {
      alert('Please record the performance duration before completing.');
      return;
    }
    
    try {
      // First, save the current freediver's data to lastSavedResult before completing
      const currentPassage = currentStatus?.current_passage;
      if (currentPassage) {
        const freediver = currentPassage.registration?.freediver || currentPassage.freediver;
        const duration = Math.floor(performanceChronoTime || stopwatchTime / 1000);
        
        const result = {
          freediver_name: `${freediver?.first_name || ''} ${freediver?.last_name || ''}`.trim() || 'Unknown',
          official_top: currentPassage.official_top_time || currentPassage.countdown_time,
          announced_performance: currentPassage.announced_performance || currentPassage.registration?.announced_performance || 0,
          realized_performance: parseFloat(performanceInput) || 0,
          duration_seconds: duration,
          penalties: [...penaltiesInput],
          card_result: selectedCardResult,
          saved_at: new Date().toISOString()
        };
        
        // Save to backend first
        await updatePerformanceWithCard(
          passageId,
          performanceInput,
          duration,
          selectedCardResult,
          penaltiesInput,
          `Performance saved for ${result.freediver_name}`
        );
        
        // Store the last saved result for display
        setLastSavedResult(result);
      }
      
      // Complete the passage (this advances to next freediver in backend)
      await axios.post(`${API_BASE_URL}/api/passages/${passageId}/complete`);
      
      // Reset all performance-related state for the next freediver
      setPerformanceInput('');
      setPenaltiesInput([]);
      setSelectedCardResult(null);
      setRpSaved(false);
      setIsRpEditable(false);
      setStopwatchTime(0);
      setPerformanceChronoTime(0);
      setPerformanceStarted(false);
      setPerformanceChronoRunning(false);
      setShowStartPerformanceButton(false);
      setRealTimeCountdown('');
      
      // Re-initialize countdown for next freediver from running order
      if (currentSession) {
        setTimeout(async () => {
          await initializeCountdownFromRunningOrder(currentSession);
        }, 500);
      }
      
      // Status will be updated in the next polling cycle
    } catch (error) {
      console.error('Error completing passage:', error);
      alert('Error completing passage. Please try again.');
    }
  };

  // Check if complete button should be enabled
  const isCompleteButtonEnabled = () => {
    return performanceInput && 
           performanceInput !== '' && 
           selectedCardResult && 
           (performanceChronoTime > 0 || stopwatchTime > 0);
  };

  const cancelPassage = async (passageId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/passages/${passageId}/cancel`);
      // Status will be updated in the next polling cycle
    } catch (error) {
      console.error('Error cancelling passage:', error);
    }
  };

  // Countdown management with real-time display
  const startCountdown = async (passageId) => {
    try {
      await axios.post(`${API_BASE_URL}/api/passages/${passageId}/start-countdown`);
      startCountdownSequence(passageId);
    } catch (error) {
      console.error('Error starting countdown:', error);
    }
  };

  const formatTime = (milliseconds) => {
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    const ms = Math.floor((milliseconds % 1000) / 10);
    
    if (minutes > 0) {
      return `${minutes}:${seconds.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
    } else {
      return `${seconds}.${ms.toString().padStart(2, '0')}s`;
    }
  };

  const startCountdownSequence = (passageId) => {
    const countdownPhases = [
      { phase: '2min', displayTime: '2:00', realDuration: 90000 }, // 90 seconds to next phase
      { phase: '1min30s', displayTime: '1:30', realDuration: 30000 }, // 30 seconds to next
      { phase: '1min', displayTime: '1:00', realDuration: 30000 },
      { phase: '30s', displayTime: '0:30', realDuration: 10000 },
      { phase: '20s', displayTime: '0:20', realDuration: 10000 },
      { phase: '10s', displayTime: '0:10', realDuration: 5000 },
      { phase: '5s', displayTime: '0:05', realDuration: 1000 },
      { phase: '4s', displayTime: '0:04', realDuration: 1000 },
      { phase: '3s', displayTime: '0:03', realDuration: 1000 },
      { phase: '2s', displayTime: '0:02', realDuration: 1000 },
      { phase: '1s', displayTime: '0:01', realDuration: 1000 },
      { phase: 'official_top', displayTime: 'OFFICIAL TOP', realDuration: 1000 }
    ];

    let currentPhaseIndex = 0;
    let timeRemaining = 120000; // 2 minutes in milliseconds

    // Clear any existing countdown interval
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }

    const updateCountdown = async () => {
      if (currentPhaseIndex >= countdownPhases.length) {
        // Start post-official-top sequence
        startPostOfficialTopSequence(passageId);
        return;
      }

      const currentPhase = countdownPhases[currentPhaseIndex];
      
      // Update backend
      try {
        await axios.post(`${API_BASE_URL}/api/passages/${passageId}/update-countdown`, null, {
          params: { phase: currentPhase.phase }
        });
      } catch (error) {
        console.error('Error updating countdown phase:', error);
      }

      // Update display
      setRealTimeCountdown(currentPhase.displayTime);

      // Start performance stopwatch when official top is called
      if (currentPhase.phase === 'official_top') {
        startStopwatch();
      }

      currentPhaseIndex++;
      
      if (currentPhaseIndex < countdownPhases.length) {
        setTimeout(updateCountdown, currentPhase.realDuration);
      }
    };

    updateCountdown();
  };

  const startPostOfficialTopSequence = (passageId) => {
    const postSequence = [
      { phase: '+1s', displayTime: '+0:01', duration: 1000 },
      { phase: '+2s', displayTime: '+0:02', duration: 1000 },
      { phase: '+3s', displayTime: '+0:03', duration: 1000 },
      { phase: '+4s', displayTime: '+0:04', duration: 1000 },
      { phase: '+5s', displayTime: '+0:05', duration: 1000 },
      { phase: '+6s', displayTime: '+0:06', duration: 1000 },
      { phase: '+7s', displayTime: '+0:07', duration: 1000 },
      { phase: '+8s', displayTime: '+0:08', duration: 1000 },
      { phase: '+9s', displayTime: '+0:09', duration: 1000 },
      { phase: '+10s', displayTime: '+0:10', duration: 10000 },
      { phase: '+20s', displayTime: '+0:20', duration: 5000 },
      { phase: '+25s', displayTime: '+0:25', duration: 1000 },
      { phase: '+26s', displayTime: '+0:26', duration: 1000 },
      { phase: '+27s', displayTime: '+0:27', duration: 1000 },
      { phase: '+28s', displayTime: '+0:28', duration: 1000 },
      { phase: '+29s', displayTime: '+0:29', duration: 1000 },
      { phase: '+30s', displayTime: '+0:30', duration: 0 }
    ];

    let currentIndex = 0;

    const executePostSequence = async () => {
      if (currentIndex < postSequence.length) {
        const { phase, displayTime, duration } = postSequence[currentIndex];
        
        try {
          await axios.post(`${API_BASE_URL}/api/passages/${passageId}/update-countdown`, null, {
            params: { phase }
          });
        } catch (error) {
          console.error('Error updating post-official countdown phase:', error);
        }

        setRealTimeCountdown(displayTime);
        currentIndex++;
        
        if (duration > 0) {
          setTimeout(executePostSequence, duration);
        }
      }
    };

    executePostSequence();
  };

  // Stopwatch functionality - Only startable when performance entry is unlocked
  const startStopwatch = () => {
    // Check if we're within 10 seconds of Official Top or after it
    if (isPerformanceLocked) {
      alert('Performance Time can only be started after Official Top is reached (or in the last 10 seconds before it).');
      return;
    }
    
    setStopwatchTime(0);
    setStopwatchRunning(true);
    
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTime;
      setStopwatchTime(elapsedTime);
    }, 10); // Update every 10ms for smooth display
    
    setStopwatchInterval(interval);
  };

  const stopStopwatch = () => {
    setStopwatchRunning(false);
    if (stopwatchInterval) {
      clearInterval(stopwatchInterval);
      setStopwatchInterval(null);
    }
  };

  const resetStopwatch = () => {
    setStopwatchTime(0);
    setStopwatchRunning(false);
    if (stopwatchInterval) {
      clearInterval(stopwatchInterval);
      setStopwatchInterval(null);
    }
  };

  // Running order functions
  const fetchRunningOrder = async (sessionId) => {
    try {
      console.log('Fetching running order for session:', sessionId);
      setShowRunningOrder(false); // Reset first
      setRunningOrder(null); // Clear previous data
      
      const response = await axios.get(`${API_BASE_URL}/api/event-sessions/${sessionId}/running-order`);
      console.log('Running order response:', response.data);
      
      if (response.data && response.data.running_order) {
        setRunningOrder(response.data);
        setShowRunningOrder(true); // Set show flag after data is loaded
        console.log('Running order state updated successfully');
      } else {
        console.error('Invalid running order response format');
        alert('No running order data available for this session.');
      }
    } catch (error) {
      console.error('Error fetching running order:', error);
      alert('Error fetching running order. Please check the session has valid data.');
      setShowRunningOrder(false);
      setRunningOrder(null);
    }
  };

  const updatePerformanceWithCard = async (passageId, distance, timeSeconds, cardResult, penalties = [], notes = '') => {
    try {
      const performanceData = {
        distance_covered: parseFloat(distance),
        performance_time_seconds: parseFloat(timeSeconds),
        card_result: cardResult,
        penalties: penalties,
        notes: notes
      };

      await axios.post(`${API_BASE_URL}/api/passages/${passageId}/update-performance`, performanceData);
      
      // Refresh running order to show updated data
      if (runningOrder && runningOrder.session) {
        await fetchRunningOrder(runningOrder.session.id);
        
        // Check if all freedivers have completed their performances
        const updatedRunningOrder = await axios.get(`${API_BASE_URL}/api/event-sessions/${runningOrder.session.id}/running-order`);
        if (checkEventCompletion(updatedRunningOrder.data)) {
          // Show results button notification
          setTimeout(() => {
            if (confirm('All freedivers have completed their performances! Would you like to view the event results?')) {
              generateEventResults(runningOrder.session.id);
            }
          }, 1000);
        }
      }
      
      // Clear form inputs
      setPerformanceInput('');
      setSelectedCardResult('');
      setPenaltiesInput([]);
      
      alert('Performance updated successfully with card result!');
    } catch (error) {
      console.error('Error updating performance with card:', error);
      alert('Error updating performance. Please try again.');
    }
  };

  // Helper function to format time for display (MM:SS format)
  // Force start event at current time
  const forceStartEventNow = async (sessionId) => {
    try {
      // First check if running order exists
      const checkRunningOrderResponse = await axios.get(`${API_BASE_URL}/api/event-sessions/${sessionId}/running-order`);
      const checkRunningOrder = checkRunningOrderResponse.data.running_order;
      
      if (!checkRunningOrder || checkRunningOrder.length === 0) {
        alert('No running order found. Please create running order first.');
        return;
      }
      
      // Use the actual force-start endpoint (sets actual_start_time = NOW, status = active)
      await axios.post(`${API_BASE_URL}/api/event-sessions/${sessionId}/force-start`);
      
      // Refresh the sessions list
      await fetchEventSessions();
      
      // Set current session and start monitoring
      setCurrentSession(sessionId);
      
      // Start global event timer with current time
      const now = new Date();
      setEventStartTime(now.toISOString());
      
      // Fetch the running order - times are recalculated based on actual_start_time (NOW)
      const runningOrderResponse = await axios.get(`${API_BASE_URL}/api/event-sessions/${sessionId}/running-order`);
      const roData = runningOrderResponse.data;
      setRunningOrder(roData);
      
      // Use server-calculated seconds_to_official_top (avoids timezone issues)
      const passages = roData.running_order || [];
      const firstPassage = passages.find(p => !p.card_result && p.status !== 'completed') || passages[0];
      if (firstPassage && firstPassage.seconds_to_official_top !== undefined) {
        const secondsUntilTop = firstPassage.seconds_to_official_top;
        if (secondsUntilTop > 0) {
          setCountdownToNext(secondsUntilTop);
          setCountdownActive(true);
          setCountdownPaused(false);
        } else {
          setCountdownToNext(0);
          setCountdownActive(false);
        }
      }
      
      // Refresh active live session banner
      await fetchActiveLiveSession();
      
      // Fetch session status
      try {
        const statusRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${sessionId}/current-status`);
        setCurrentStatus(statusRes.data);
      } catch (e) {
        console.log('Status fetch optional:', e);
      }
      
      // Navigate to Live Events tab
      setActiveTab('live-events');
      
      alert(language === 'fr' ? 'Evenement demarre!' : 'Event started successfully!');
    } catch (error) {
      console.error('Error force starting event:', error);
      alert(error.response?.data?.detail || 'Error starting event. Please try again.');
    }
  };

  // Countdown timer for passages - Initialize countdown
  const startCountdownTimer = (sessionId, intervalMinutes) => {
    const secondsRemaining = intervalMinutes * 60; // Convert to seconds
    setCountdownToNext(secondsRemaining);
    setCountdownActive(true);
    setCountdownPaused(false);
    setIsPerformanceLocked(true); // Lock performance entry initially
  };

  // Generate event results with gender filter
  const generateEventResults = async (sessionId, gender = null) => {
    try {
      let url = `${API_BASE_URL}/api/event-sessions/${sessionId}/rankings`;
      if (gender && gender !== 'all') {
        url += `?gender=${gender}`;
      }
      
      const response = await axios.get(url);
      const rankingsData = response.data;
      
      setEventResults({
        session: rankingsData.session,
        competition: rankingsData.competition,
        event_type: rankingsData.event_type,
        rankings: rankingsData.rankings,
        total_competitors: rankingsData.total_competitors,
        gender_filter: rankingsData.gender_filter || gender
      });
      setShowResults(true);
    } catch (error) {
      console.error('Error generating results:', error);
      // Fallback to running-order endpoint
      try {
        const response = await axios.get(`${API_BASE_URL}/api/event-sessions/${sessionId}/running-order`);
        const resultsData = response.data;
        
        let sortedResults = [...resultsData.running_order].sort((a, b) => {
          const scoreA = a.final_score || 0;
          const scoreB = b.final_score || 0;
          return scoreB - scoreA;
        });
        
        if (gender && gender !== 'all') {
          sortedResults = sortedResults.filter(p => p.registration?.freediver?.gender === gender);
        }
        
        // Convert to rankings format
        const rankings = sortedResults.map((p, idx) => ({
          rank: idx + 1,
          passage_id: p.id,
          competitor_name: `${p.registration?.freediver?.first_name || ''} ${p.registration?.freediver?.last_name || ''}`,
          first_name: p.registration?.freediver?.first_name || '',
          last_name: p.registration?.freediver?.last_name || '',
          gender: p.registration?.freediver?.gender || 'U',
          nation: p.registration?.freediver?.nationality || '',
          announced_performance: p.registration?.announced_performance || 0,
          realized_performance: p.distance_covered,
          performance_time_seconds: p.performance_time_seconds,
          card_result: p.card_result || '',
          is_dq: p.is_dq || false,
          penalties: p.penalties || [],
          penalty_details: p.penalty_details || {},
          final_score: p.final_score || 0,
          status: p.status || ''
        }));
        
        setEventResults({
          session: resultsData.session,
          rankings: rankings,
          total_competitors: rankings.length,
          gender_filter: gender
        });
        setShowResults(true);
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        alert('Error generating results. Please try again.');
      }
    }
  };

  // Refresh results with new gender filter
  const refreshResultsWithGender = async (gender) => {
    if (eventResults?.session?.id) {
      setResultsGenderFilter(gender);
      await generateEventResults(eventResults.session.id, gender);
    }
  };

  // Wrapper function for completing a passage from LiveEventsTab
  const handlePassageComplete = async (passage, status) => {
    if (!passage || !passage.id) {
      console.error('Invalid passage data');
      return;
    }
    try {
      await axios.post(`${API_BASE_URL}/api/passages/${passage.id}/complete`);
      // Refresh status
      if (currentSession) {
        const statusRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${currentSession}/current-status`);
        setCurrentStatus(statusRes.data);
      }
    } catch (error) {
      console.error('Error completing passage:', error);
      alert('Error completing passage. Please try again.');
    }
  };

  // Wrapper function for card passage from LiveEventsTab
  const handlePassageCard = async (passage, cardType) => {
    if (!passage || !passage.id) {
      console.error('Invalid passage data');
      return;
    }
    try {
      await axios.put(`${API_BASE_URL}/api/passages/${passage.id}`, {
        card_result: cardType
      });
      // Refresh status
      if (currentSession) {
        const statusRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${currentSession}/current-status`);
        setCurrentStatus(statusRes.data);
      }
    } catch (error) {
      console.error('Error updating passage card:', error);
      alert('Error updating passage. Please try again.');
    }
  };

  // Save performance with federation-aware penalty calculation
  const savePerformanceWithPenalties = async (data) => {
    try {
      await axios.post(`${API_BASE_URL}/api/passages/${data.passageId}/performance-enhanced`, {
        distance_covered: data.distance_covered,
        performance_time_seconds: data.performance_time_seconds || null,
        card_result: data.card_result,
        penalty_details: data.penalty_details,
        notes: data.notes || '',
        speed_splits: data.speed_splits || []
      });
      // Refresh session status
      if (currentSession) {
        const statusRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${currentSession}/current-status`);
        setCurrentStatus(statusRes.data);
      }
    } catch (error) {
      console.error('Error saving performance:', error);
      alert(error.response?.data?.detail || 'Error saving performance');
    }
  };

  // Reset active session to pending (simulation mode)
  const resetActiveSessionToPending = async (sessionId) => {
    const targetId = sessionId || currentSession;
    if (!targetId) return;
    if (!confirm(language === 'fr'
      ? 'Arreter cette session et la remettre en attente? Toutes les performances seront effacees.'
      : 'Stop this session and reset to pending? All performance data will be cleared.')) return;
    try {
      await axios.post(`${API_BASE_URL}/api/event-sessions/${targetId}/stop`);
      // Clear current session state
      if (currentSession === targetId) {
        setCurrentSession(null);
        setCurrentStatus(null);
        setEventStartTime(null);
        setEventElapsedTime(0);
        setCountdownToNext(null);
        setCountdownActive(false);
        setRunningOrder(null);
      }
      await fetchEventSessions();
      await fetchActiveLiveSession();
      alert(language === 'fr' ? 'Session arretee et remise en attente' : 'Session stopped and reset to pending');
    } catch (error) {
      console.error('Error stopping session:', error);
      alert(error.response?.data?.detail || 'Error stopping session');
    }
  };


  // Skip to next countdown (simulation mode)
  const skipToNextCountdown = async () => {
    if (!currentSession) return;
    try {
      const response = await axios.post(`${API_BASE_URL}/api/event-sessions/${currentSession}/skip-to-next`);
      const { passage_order, countdown_seconds, freediver_name } = response.data;
      
      // Refresh running order with recalculated times
      const roRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${currentSession}/running-order`);
      setRunningOrder(roRes.data);
      
      // Start countdown at the federation's countdown duration (2 or 3 min)
      setCountdownToNext(countdown_seconds || 180);
      setCountdownActive(true);
      setCountdownPaused(false);
      
      // Refresh status to get updated passage
      try {
        const statusRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${currentSession}/current-status`);
        setCurrentStatus(statusRes.data);
      } catch (e) { /* optional */ }
      
      alert(language === 'fr' 
        ? `Saut au passage #${passage_order} (${freediver_name}) - debut du compte a rebours` 
        : `Skipped to passage #${passage_order} (${freediver_name}) - countdown started`);
    } catch (error) {
      console.error('Error skipping:', error);
      alert(error.response?.data?.detail || 'Error skipping to next passage');
    }
  };

  // Export results to PDF
  const exportResultsToPDF = () => {
    if (!eventResults) {
      alert('No results data available to export');
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      
      // Header with logo placeholder and title
      doc.setFillColor(20, 83, 109); // Teal color
      doc.rect(0, 0, pageWidth, 40, 'F');
      
      // Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(20);
      doc.setFont(undefined, 'bold');
      doc.text('Apnea Coach Manager', 15, 20);
      doc.setFontSize(16);
      doc.text('Event Results', 15, 32);
      
      // Event details
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      
      const eventInfo = [
        `Event: ${eventResults.session?.name || 'N/A'}`,
        `Type: ${eventResults.session?.event_type?.name || 'N/A'}`,
        `Category: ${eventResults.session?.category || 'N/A'}`,
        `Date: ${eventResults.session?.start_time ? new Date(eventResults.session.start_time).toLocaleDateString() : 'N/A'}`,
        `Participants: ${eventResults.total_participants || 0}`
      ];

      let yPosition = 55;
      eventInfo.forEach((info) => {
        doc.text(info, 15, yPosition);
        yPosition += 7;
      });

      // Results table
      const tableData = eventResults.running_order?.map((passage, index) => [
        `#${index + 1}`,
        `${passage.registration?.freediver?.first_name || 'Unknown'} ${passage.registration?.freediver?.last_name || 'Freediver'}`,
        passage.registration?.freediver?.nationality || 'N/A',
        `${passage.registration?.announced_performance || 'N/A'} ${eventResults.session?.event_type?.unit || ''}`,
        passage.distance_covered !== null && passage.distance_covered !== undefined 
          ? `${passage.distance_covered} ${eventResults.session?.event_type?.unit || ''}` 
          : 'N/A',
        passage.performance_time_seconds ? formatTimeDisplay(passage.performance_time_seconds) : 'N/A',
        passage.card_result ? passage.card_result.toUpperCase() : 'No Decision',
        passage.final_score !== null && passage.final_score !== undefined ? `${passage.final_score} pts` : '0 pts'
      ]) || [];

      // Use autoTable if available, otherwise create simple table
      if (doc.autoTable) {
        doc.autoTable({
          head: [['Rank', 'Freediver', 'Nationality', 'Announced Performance', 'Realized Performance', 'Time', 'Decision', 'Points']],
          body: tableData,
          startY: yPosition + 10,
          styles: {
            fontSize: 9,
            cellPadding: 3,
          },
          headStyles: {
            fillColor: [20, 83, 109],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
          },
          alternateRowStyles: {
            fillColor: [240, 248, 255],
          },
          columnStyles: {
            0: { halign: 'center', cellWidth: 15 },
            1: { cellWidth: 35 },
            2: { halign: 'center', cellWidth: 20 },
            3: { halign: 'center', cellWidth: 25 },
            4: { halign: 'center', cellWidth: 25 },
            5: { halign: 'center', cellWidth: 20 },
            6: { halign: 'center', cellWidth: 20 },
            7: { halign: 'center', cellWidth: 25 },
          }
        });

        // Footer
        const finalY = doc.lastAutoTable.finalY + 20;
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        doc.text(`Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}`, 15, finalY);
        doc.text('Apnea Coach Manager - Professional Freediving Competition Management', pageWidth - 15, finalY, { align: 'right' });
      } else {
        // Fallback if autoTable is not available
        console.warn('autoTable not available, creating basic table');
        let tableY = yPosition + 10;
        doc.setFontSize(10);
        doc.text('Results Table:', 15, tableY);
        tableY += 10;
        
        tableData.forEach((row, index) => {
          doc.text(`${index + 1}. ${row[1]} (${row[2]}) - AP: ${row[3]}, RP: ${row[4]}, Time: ${row[5]}, Card: ${row[6]}, Points: ${row[7]}`, 15, tableY);
          tableY += 7;
        });
      }

      // Save the PDF
      const fileName = `${eventResults.session?.name?.replace(/\s+/g, '_') || 'Event'}_Results_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      alert('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Error exporting PDF. Please try again.');
    }
  };

  // Calculate countdown to next freediver's timing phases
  const calculateNextFreediverCountdown = (nextPassage) => {
    if (!nextPassage) return { warmup: null, transition: null, official: null };

    const now = new Date();
    const warmupTime = new Date(nextPassage.warmup_time);
    const transitionTime = new Date(nextPassage.transition_time);
    const officialTime = new Date(nextPassage.countdown_time);

    const calculateTimeRemaining = (targetTime) => {
      const diff = targetTime - now;
      if (diff <= 0) return null;
      
      const totalSeconds = Math.floor(diff / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    return {
      warmup: calculateTimeRemaining(warmupTime),
      transition: calculateTimeRemaining(transitionTime),
      official: calculateTimeRemaining(officialTime)
    };
  };

  // Start countdown for next freediver
  const startNextFreediverCountdown = (nextPassage) => {
    // Clear existing intervals
    countdownIntervals.forEach(interval => clearInterval(interval));
    setCountdownIntervals([]);

    if (!nextPassage) {
      setNextFreediverCountdown({ warmup: null, transition: null, official: null });
      return;
    }

    // Start countdown timer
    const interval = setInterval(() => {
      if (!countdownPaused) {
        const countdown = calculateNextFreediverCountdown(nextPassage);
        setNextFreediverCountdown(countdown);
        
        // Stop countdown if all phases have passed
        if (!countdown.warmup && !countdown.transition && !countdown.official) {
          clearInterval(interval);
        }
      }
    }, 1000);

    setCountdownIntervals([interval]);
  };

  // Check if current freediver has validated RP and card result
  const isCurrentFreediverValidated = () => {
    return rpSaved && cardSaved && savedRpValue && selectedCardResult;
  };

  // Pause/Resume countdown
  const toggleCountdownPause = () => {
    if (countdownPaused) {
      // Resuming: Restart the countdown from where we left off
      setCountdownPaused(false);
      setCountdownActive(true);
    } else {
      // Pausing: Stop the countdown but keep the value
      setCountdownPaused(true);
    }
  };

  // Stop event session completely
  // Reset event session to beginning with current time
  const restartEventSession = async (sessionId) => {
    if (!confirm(language === 'fr' 
      ? 'Redemarrer cet evenement? Les performances seront effacees et les compteurs repartiront de maintenant.'
      : 'Restart this event? All performance data will be cleared and timers will restart from now.')) {
      return;
    }
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/event-sessions/${sessionId}/restart`);
      
      // Reset all frontend timers
      const now = new Date();
      setEventStartTime(now.toISOString());
      setCountdownToNext(null);
      setCountdownActive(false);
      setCountdownPaused(false);
      setStopwatchTime(0);
      setStopwatchRunning(false);
      setIsPerformanceLocked(true);
      setCurrentPassageIndex(0);
      
      // Refresh event sessions
      await fetchEventSessions();
      
      // Re-fetch running order with recalculated times based on new actual_start_time
      if (currentSession === sessionId) {
        const roRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${sessionId}/running-order`);
        setRunningOrder(roRes.data);
        
        // Recalculate countdown from new running order
        const passages = roRes.data.running_order || [];
        const nextPassage = passages.find(p => !p.card_result && p.status !== 'completed') || passages[0];
        if (nextPassage && nextPassage.official_top_time) {
          const officialTopTime = new Date(nextPassage.official_top_time).getTime();
          const secondsUntilTop = Math.max(0, Math.floor((officialTopTime - now.getTime()) / 1000));
          setCountdownToNext(secondsUntilTop);
          setCountdownActive(true);
          setCountdownPaused(false);
        }
      }
      
      alert(language === 'fr' ? 'Evenement redemarre!' : 'Event restarted successfully!');
    } catch (error) {
      console.error('Error restarting event session:', error);
      alert(error.response?.data?.detail || 'Error restarting event session');
    }
  };

  const stopEventSession = async (sessionId) => {
    const targetId = sessionId || currentSession;
    if (!targetId) return;
    if (!confirm(language === 'fr'
      ? 'Arreter cet evenement? Toutes les performances seront effacees et le statut repassera en attente.'
      : 'Stop this event? All performance data will be cleared and status will return to pending.')) return;
    try {
      await axios.post(`${API_BASE_URL}/api/event-sessions/${targetId}/stop`);
      // Clear current session state
      if (currentSession === targetId) {
        setCurrentSession(null);
        setCurrentStatus(null);
        setEventStartTime(null);
        setEventElapsedTime(0);
        setCountdownToNext(null);
        setCountdownActive(false);
        setRunningOrder(null);
      }
      await fetchEventSessions();
      await fetchActiveLiveSession();
      alert(language === 'fr' ? 'Evenement arrete et remis en attente' : 'Event stopped and reset to pending');
    } catch (error) {
      console.error('Error stopping session:', error);
      alert(error.response?.data?.detail || 'Error stopping session');
    }
  };

  // Delete event session
  const deleteEventSession = async (sessionId) => {
    if (!confirm('Are you sure you want to delete this event? This action cannot be undone and will delete all associated data.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE_URL}/api/event-sessions/${sessionId}`);
      alert('Event session deleted successfully!');
      await fetchEventSessions();
      // Clear current session if it was deleted
      if (currentSession === sessionId) {
        setCurrentSession(null);
      }
    } catch (error) {
      console.error('Error deleting event session:', error);
      alert('Error deleting event session. Please try again.');
    }
  };

  // Training session functions
  const fetchTrainingSessions = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/training-sessions`);
      setTrainingSessions(response.data.sessions);
    } catch (error) {
      console.error('Error fetching training sessions:', error);
    }
  };

  const fetchExerciseLibrary = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/exercise-library`);
      setExerciseLibrary(response.data.exercises);
    } catch (error) {
      console.error('Error fetching exercise library:', error);
    }
  };

  const fetchTrainingTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/training-templates`);
      setTrainingTemplates(response.data.templates);
    } catch (error) {
      console.error('Error fetching training templates:', error);
    }
  };

  const fetchCustomTemplates = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/custom-templates`);
      setCustomTemplates(response.data.templates || []);
    } catch (error) {
      console.error('Error fetching custom templates:', error);
    }
  };

  // ============= FOLLOW UP FUNCTIONS =============
  
  // Fetch all performances
  const fetchPerformances = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/performances`);
      setPerformances(response.data.performances || []);
    } catch (error) {
      console.error('Error fetching performances:', error);
    }
  };

  // Fetch performances for a specific freediver
  const fetchFreediverPerformances = async (freediverId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/performances/by-freediver/${freediverId}`);
      setFreediverPerformances(response.data.performances || []);
      setSelectedFreediverForFollowUp(response.data.freediver);
    } catch (error) {
      console.error('Error fetching freediver performances:', error);
    }
  };

  // Fetch certification levels for an agency
  const fetchCertificationLevels = async (agency) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/certification-levels/${agency}`);
      setCertificationLevels(response.data.levels || []);
    } catch (error) {
      console.error('Error fetching certification levels:', error);
      setCertificationLevels([]);
    }
  };

  // Calculate age from date of birth
  const calculateAge = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Format duration seconds to minutes:seconds
  const formatPerformanceDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Create a new performance
  const createPerformance = async () => {
    try {
      const durationSeconds = (parseInt(newPerformance.duration_minutes) || 0) * 60 + (parseInt(newPerformance.duration_secs) || 0);
      
      // Get coach name from coach_id
      const coach = coaches.find(c => c.id === newPerformance.coach_id);
      const coachName = coach ? `${coach.first_name} ${coach.last_name}` : '';
      
      const performanceData = {
        freediver_id: newPerformance.freediver_id,
        performance_date: new Date(newPerformance.performance_date).toISOString(),
        location: newPerformance.location,
        pool_type: newPerformance.pool_type,
        coach_id: newPerformance.coach_id || null,
        coach_name: coachName,
        performance_type: newPerformance.performance_type,
        distance: newPerformance.performance_type === 'STA' ? null : parseFloat(newPerformance.distance) || 0,
        duration_seconds: durationSeconds,
        comments: newPerformance.comments,
        preparation_protocol: newPerformance.preparation_protocol
      };

      await axios.post(`${API_BASE_URL}/api/performances`, performanceData);
      alert('Performance created successfully!');
      setShowPerformanceModal(false);
      resetPerformanceForm();
      await fetchPerformances();
      if (selectedFreediverForFollowUp) {
        await fetchFreediverPerformances(selectedFreediverForFollowUp.id);
      }
    } catch (error) {
      console.error('Error creating performance:', error);
      alert('Error creating performance. Please try again.');
    }
  };

  // Update an existing performance tracking record
  const updatePerformanceTracking = async () => {
    if (!editingPerformance) return;
    
    try {
      const durationSeconds = (parseInt(newPerformance.duration_minutes) || 0) * 60 + (parseInt(newPerformance.duration_secs) || 0);
      
      // Get coach name from coach_id
      const coach = coaches.find(c => c.id === newPerformance.coach_id);
      const coachName = coach ? `${coach.first_name} ${coach.last_name}` : '';
      
      const performanceData = {
        freediver_id: newPerformance.freediver_id,
        performance_date: new Date(newPerformance.performance_date).toISOString(),
        location: newPerformance.location,
        pool_type: newPerformance.pool_type,
        coach_id: newPerformance.coach_id || null,
        coach_name: coachName,
        performance_type: newPerformance.performance_type,
        distance: newPerformance.performance_type === 'STA' ? null : parseFloat(newPerformance.distance) || 0,
        duration_seconds: durationSeconds,
        comments: newPerformance.comments,
        preparation_protocol: newPerformance.preparation_protocol
      };

      await axios.put(`${API_BASE_URL}/api/performances/${editingPerformance.id}`, performanceData);
      alert('Performance updated successfully!');
      setShowPerformanceModal(false);
      setEditingPerformance(null);
      resetPerformanceForm();
      await fetchPerformances();
      if (selectedFreediverForFollowUp) {
        await fetchFreediverPerformances(selectedFreediverForFollowUp.id);
      }
    } catch (error) {
      console.error('Error updating performance:', error);
      alert('Error updating performance. Please try again.');
    }
  };

  // Delete a performance
  const deletePerformance = async (performanceId) => {
    if (!window.confirm('Are you sure you want to delete this performance? This action cannot be undone.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_BASE_URL}/api/performances/${performanceId}`);
      alert('Performance deleted successfully!');
      await fetchPerformances();
      if (selectedFreediverForFollowUp) {
        await fetchFreediverPerformances(selectedFreediverForFollowUp.id);
      }
    } catch (error) {
      console.error('Error deleting performance:', error);
      alert('Error deleting performance. Please try again.');
    }
  };

  // Open performance modal for editing
  const openEditPerformanceModal = (performance) => {
    setEditingPerformance(performance);
    const mins = Math.floor((performance.duration_seconds || 0) / 60);
    const secs = (performance.duration_seconds || 0) % 60;
    
    setNewPerformance({
      freediver_id: performance.freediver_id,
      performance_date: performance.performance_date?.split('T')[0] || '',
      location: performance.location || '',
      pool_type: performance.pool_type || '25m',
      coach_id: performance.coach_id || '',
      performance_type: performance.performance_type || 'DYN',
      distance: performance.distance?.toString() || '',
      duration_minutes: mins.toString(),
      duration_secs: secs.toString(),
      comments: performance.comments || '',
      preparation_protocol: performance.preparation_protocol || ''
    });
    setShowPerformanceModal(true);
  };

  // Open performance modal for new entry
  const openNewPerformanceModal = (freediverId = '') => {
    setEditingPerformance(null);
    resetPerformanceForm();
    // Pre-fill with current date
    const today = new Date().toISOString().split('T')[0];
    if (freediverId) {
      setNewPerformance(prev => ({ ...prev, freediver_id: freediverId, performance_date: today }));
    } else {
      setNewPerformance(prev => ({ ...prev, performance_date: today }));
    }
    setShowPerformanceModal(true);
  };

  // Reset performance form
  const resetPerformanceForm = () => {
    const today = new Date().toISOString().split('T')[0];
    setNewPerformance({
      freediver_id: '',
      performance_date: today,
      location: '',
      pool_type: '25m',
      coach_id: '', // Changed from coach_name to coach_id
      performance_type: 'DYN',
      distance: '',
      duration_minutes: '',
      duration_secs: '',
      comments: '',
      preparation_protocol: ''
    });
  };

  // Aquatic Infrastructure Handlers
  const resetInfrastructureForm = () => {
    setInfrastructureForm({
      short_name: '',
      long_name: '',
      pool_type: '25m',
      pool_length: '',
      min_depth: '',
      max_depth: '',
      number_of_lanes: '',
      max_capacity: '',
      logo_url: '',
      website_url: '',
      address_number: '',
      address_street: '',
      address_type: 'Street',
      city: '',
      postal_code: '',
      province: '',
      country: '',
      organization: '',
      building_name: ''
    });
    setInfrastructureLogoPreview(null);
  };

  const handleInfrastructureLogoUpload = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('Logo file size must be less than 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setInfrastructureLogoPreview(reader.result);
        setInfrastructureForm({...infrastructureForm, logo_url: reader.result});
      };
      reader.readAsDataURL(file);
    }
  };

  const clearInfrastructureLogo = () => {
    setInfrastructureLogoPreview(null);
    setInfrastructureForm({...infrastructureForm, logo_url: ''});
    if (infrastructureLogoInputRef.current) {
      infrastructureLogoInputRef.current.value = '';
    }
  };

  const handleCreateInfrastructure = async (e) => {
    e.preventDefault();
    try {
      const infrastructureData = {
        ...infrastructureForm,
        pool_length: parseFloat(infrastructureForm.pool_length) || 0,
        min_depth: parseFloat(infrastructureForm.min_depth) || 0,
        max_depth: parseFloat(infrastructureForm.max_depth) || 0,
        number_of_lanes: parseInt(infrastructureForm.number_of_lanes) || 0,
        max_capacity: parseInt(infrastructureForm.max_capacity) || 0
      };

      await axios.post(`${API_BASE_URL}/api/aquatic-infrastructure`, infrastructureData);
      alert('Aquatic infrastructure created successfully!');
      setShowInfrastructureModal(false);
      resetInfrastructureForm();
      fetchData();
    } catch (error) {
      console.error('Error creating infrastructure:', error);
      alert('Error creating aquatic infrastructure. Please try again.');
    }
  };

  const handleUpdateInfrastructure = async (e) => {
    e.preventDefault();
    try {
      const infrastructureData = {
        ...infrastructureForm,
        pool_length: parseFloat(infrastructureForm.pool_length) || 0,
        min_depth: parseFloat(infrastructureForm.min_depth) || 0,
        max_depth: parseFloat(infrastructureForm.max_depth) || 0,
        number_of_lanes: parseInt(infrastructureForm.number_of_lanes) || 0,
        max_capacity: parseInt(infrastructureForm.max_capacity) || 0
      };

      await axios.put(`${API_BASE_URL}/api/aquatic-infrastructure/${editingInfrastructure.id}`, infrastructureData);
      alert('Aquatic infrastructure updated successfully!');
      setShowInfrastructureModal(false);
      setEditingInfrastructure(null);
      resetInfrastructureForm();
      fetchData();
    } catch (error) {
      console.error('Error updating infrastructure:', error);
      alert('Error updating aquatic infrastructure. Please try again.');
    }
  };

  const handleDeleteInfrastructure = async (infrastructureId) => {
    if (!window.confirm('Are you sure you want to delete this aquatic infrastructure?')) {
      return;
    }
    try {
      await axios.delete(`${API_BASE_URL}/api/aquatic-infrastructure/${infrastructureId}`);
      alert('Aquatic infrastructure deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting infrastructure:', error);
      alert('Error deleting aquatic infrastructure. Please try again.');
    }
  };

  const openNewInfrastructureModal = () => {
    setEditingInfrastructure(null);
    resetInfrastructureForm();
    setShowInfrastructureModal(true);
  };

  const openEditInfrastructureModal = (infrastructure) => {
    setEditingInfrastructure(infrastructure);
    setInfrastructureForm({
      short_name: infrastructure.short_name || '',
      long_name: infrastructure.long_name || '',
      pool_type: infrastructure.pool_type || '25m',
      pool_length: infrastructure.pool_length?.toString() || '',
      min_depth: infrastructure.min_depth?.toString() || '',
      max_depth: infrastructure.max_depth?.toString() || '',
      number_of_lanes: infrastructure.number_of_lanes?.toString() || '',
      max_capacity: infrastructure.max_capacity?.toString() || '',
      logo_url: infrastructure.logo_url || '',
      website_url: infrastructure.website_url || '',
      address_number: infrastructure.address_number || '',
      address_street: infrastructure.address_street || '',
      address_type: infrastructure.address_type || 'Street',
      city: infrastructure.city || '',
      postal_code: infrastructure.postal_code || '',
      province: infrastructure.province || '',
      country: infrastructure.country || '',
      organization: infrastructure.organization || '',
      building_name: infrastructure.building_name || ''
    });
    setInfrastructureLogoPreview(infrastructure.logo_url || null);
    setShowInfrastructureModal(true);
  };

  // Get ranking icon (Gold, Silver, Bronze)
  const getRankingIcon = (ranking) => {
    if (ranking === 1) return '🥇';
    if (ranking === 2) return '🥈';
    if (ranking === 3) return '🥉';
    return '';
  };

  // Group performances by type and calculate stats per type
  const getPerformanceStatsByType = (performances) => {
    const byType = {};
    performances.forEach(perf => {
      const type = perf.performance_type;
      if (!byType[type]) {
        byType[type] = { 
          gold: 0, silver: 0, bronze: 0, total: 0, 
          goldValue: null, silverValue: null, bronzeValue: null,
          goldDuration: null, silverDuration: null, bronzeDuration: null
        };
      }
      byType[type].total++;
      
      // Store values based on ranking
      if (perf.ranking === 1) {
        byType[type].gold++;
        byType[type].goldValue = type === 'STA' ? null : perf.distance;
        byType[type].goldDuration = perf.duration_seconds;
      }
      if (perf.ranking === 2) {
        byType[type].silver++;
        byType[type].silverValue = type === 'STA' ? null : perf.distance;
        byType[type].silverDuration = perf.duration_seconds;
      }
      if (perf.ranking === 3) {
        byType[type].bronze++;
        byType[type].bronzeValue = type === 'STA' ? null : perf.distance;
        byType[type].bronzeDuration = perf.duration_seconds;
      }
    });
    return byType;
  };

  // Filter freedivers based on search query for Follow Up
  const filteredFreediversForFollowUp = freedivers.filter(freediver => {
    if (!followUpSearchQuery.trim()) return true;
    const query = followUpSearchQuery.toLowerCase();
    const fullName = `${freediver.first_name} ${freediver.last_name}`.toLowerCase();
    return fullName.includes(query) || 
           freediver.nationality?.toLowerCase().includes(query) ||
           freediver.email?.toLowerCase().includes(query);
  });

  // Filter freedivers based on search, nationality and gender for Freedivers tab
  const filteredFreedivers = freedivers.filter(freediver => {
    // Search query filter
    if (freediversSearchQuery.trim()) {
      const query = freediversSearchQuery.toLowerCase();
      const fullName = `${freediver.first_name} ${freediver.last_name}`.toLowerCase();
      const matchesSearch = fullName.includes(query) || 
             freediver.nationality?.toLowerCase().includes(query) ||
             freediver.email?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }
    
    // Nationality filter
    if (freediversNationalityFilter !== 'all' && freediver.nationality !== freediversNationalityFilter) {
      return false;
    }
    
    // Gender filter
    if (freediversGenderFilter !== 'all' && freediver.gender !== freediversGenderFilter) {
      return false;
    }
    
    return true;
  });

  // Get unique nationalities from freedivers for filter dropdown
  const uniqueNationalities = [...new Set(freedivers.map(f => f.nationality).filter(Boolean))].sort();

  // Handler to navigate to Follow Up tab and select freediver
  const navigateToFreediverPerformances = (freediver) => {
    setSelectedFreediverForFollowUp(freediver);
    setActiveTab('follow-up');
    fetchFreediverPerformances(freediver.id);
  };

  // === WRAPPER FUNCTIONS FOR EXTRACTED COMPONENTS ===
  
  // Infrastructure handlers for AquaticInfrastructureTab
  const handleCreateInfrastructureWrapper = async (infrastructureData) => {
    try {
      await axios.post(`${API_BASE_URL}/api/aquatic-infrastructure`, infrastructureData);
      alert('Aquatic infrastructure created successfully!');
      fetchData();
    } catch (error) {
      console.error('Error creating infrastructure:', error);
      alert('Error creating aquatic infrastructure. Please try again.');
    }
  };

  const handleUpdateInfrastructureWrapper = async (id, infrastructureData) => {
    try {
      await axios.put(`${API_BASE_URL}/api/aquatic-infrastructure/${id}`, infrastructureData);
      alert('Aquatic infrastructure updated successfully!');
      fetchData();
    } catch (error) {
      console.error('Error updating infrastructure:', error);
      alert('Error updating aquatic infrastructure. Please try again.');
    }
  };

  const handleDeleteInfrastructureWrapper = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/aquatic-infrastructure/${id}`);
      alert('Aquatic infrastructure deleted successfully!');
      fetchData();
    } catch (error) {
      console.error('Error deleting infrastructure:', error);
      alert('Error deleting aquatic infrastructure. Please try again.');
    }
  };

  // Performance handlers for FollowUpTab
  const handleCreatePerformanceWrapper = async (performanceData) => {
    try {
      await axios.post(`${API_BASE_URL}/api/performances`, performanceData);
      // Refresh data
      await fetchPerformances();
      if (selectedFreediverForFollowUp) {
        await fetchFreediverPerformances(selectedFreediverForFollowUp.id);
      }
      return true; // Success
    } catch (error) {
      console.error('Error creating performance:', error);
      alert(t('error') + ': ' + (error.response?.data?.detail || 'Error creating performance. Please try again.'));
      throw error; // Re-throw to let caller know it failed
    }
  };

  const handleUpdatePerformanceWrapper = async (id, performanceData) => {
    try {
      await axios.put(`${API_BASE_URL}/api/performances/${id}`, performanceData);
      // Refresh data
      await fetchPerformances();
      if (selectedFreediverForFollowUp) {
        await fetchFreediverPerformances(selectedFreediverForFollowUp.id);
      }
      return true; // Success
    } catch (error) {
      console.error('Error updating performance:', error);
      alert(t('error') + ': ' + (error.response?.data?.detail || 'Error updating performance. Please try again.'));
      throw error; // Re-throw to let caller know it failed
    }
  };

  const handleDeletePerformanceWrapper = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/performances/${id}`);
      // Refresh data
      await fetchPerformances();
      if (selectedFreediverForFollowUp) {
        await fetchFreediverPerformances(selectedFreediverForFollowUp.id);
      }
      return true; // Success
    } catch (error) {
      console.error('Error deleting performance:', error);
      alert(t('error') + ': ' + (error.response?.data?.detail || 'Error deleting performance. Please try again.'));
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-950 via-blue-900 to-teal-800 text-white">
      {/* Underwater Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 animate-float opacity-20">
          <Fish size={40} className="text-blue-300" />
        </div>
        <div className="absolute top-40 right-20 animate-float-delayed opacity-15">
          <Waves size={60} className="text-teal-300" />
        </div>
        <div className="absolute bottom-20 left-1/4 animate-float opacity-10">
          <Anchor size={50} className="text-blue-400" />
        </div>
        <div className="absolute top-1/3 right-1/3 animate-pulse opacity-5">
          <div className="w-32 h-32 rounded-full bg-gradient-to-r from-blue-400 to-teal-400"></div>
        </div>
      </div>

      {/* Header */}
      <header className="relative z-10 bg-gradient-to-r from-blue-900/80 to-teal-800/80 backdrop-blur-md border-b border-blue-700/30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-r from-blue-500 to-teal-500 rounded-lg">
                <Waves size={28} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-200 to-teal-200 bg-clip-text text-transparent">
                  {t('appTitle')}
                </h1>
                <p className="text-sm text-blue-300">{t('appSubtitle')}</p>
              </div>
            </div>
            
            {/* Right side: Language Switcher + User info + Settings + Banner */}
            <div className="flex items-center space-x-4">
              {/* Language Switcher */}
              <div className="relative" style={{ zIndex: 10001 }}>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="w-[100px] bg-blue-800/50 border-blue-600 text-blue-200 h-9" data-testid="language-switcher">
                    <Languages size={16} className="mr-1" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-blue-800 border-blue-600" style={{ zIndex: 10002 }} position="popper" side="bottom" align="end">
                    <SelectItem value="en">
                      <span className="flex items-center">
                        <span className="mr-2">🇬🇧</span> EN
                      </span>
                    </SelectItem>
                    <SelectItem value="fr">
                      <span className="flex items-center">
                        <span className="mr-2">🇫🇷</span> FR
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* User Display / Login Button */}
              {isAuthenticated ? (
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2 bg-blue-800/50 rounded-lg px-3 py-2">
                    <User size={18} className="text-teal-400" />
                    <span className="text-blue-200 font-medium">{currentUser?.display_name || currentUser?.username}</span>
                    {currentUser?.role === 'admin' && (
                      <Badge className="bg-teal-700/50 text-teal-200 text-xs ml-1">
                        <Shield size={12} className="mr-1" />
                        {t('admin')}
                      </Badge>
                    )}
                  </div>
                  {/* Settings Button (Admin only) - User Management */}
                  {currentUser?.role === 'admin' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setShowUserManagement(true); }}
                      className="text-blue-300 hover:text-white hover:bg-blue-700/50"
                      title={t('userManagement') || 'User Management'}
                      data-testid="user-management-button"
                    >
                      <Settings size={20} />
                    </Button>
                  )}
                  {/* Global Settings Button (Admin only) */}
                  {currentUser?.role === 'admin' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { fetchSettings(); setShowSettingsModal(true); }}
                      className="text-blue-300 hover:text-white hover:bg-blue-700/50"
                      title={t('globalSettings') || 'Global Settings'}
                      data-testid="global-settings-button"
                    >
                      <SlidersHorizontal size={20} />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleLogout}
                    className="text-blue-300 hover:text-white hover:bg-blue-700/50"
                    title={t('disconnect')}
                  >
                    <LogOut size={18} />
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={() => setShowLoginModal(true)}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  <LogIn size={18} className="mr-2" />
                  {t('connect')}
                </Button>
              )}
              
              {/* Banner Image */}
              <img 
                src="https://customer-assets.emergentagent.com/job_tracker-apnea/artifacts/l39g84vu_Bandeau%20Apnea%20Coach%20Manager%20Petit.png" 
                alt="Apnea Coach Manager Banner"
                className="h-16 object-contain"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="relative bg-black/20 backdrop-blur-sm border-b border-blue-700/20" style={{ zIndex: 1000 }}>
        <div className="container mx-auto px-6">
          <Tabs value={activeTab} onValueChange={(value) => {
            // Only allow dashboard and freediving-levels if not authenticated
            if (!isAuthenticated && value !== 'dashboard' && value !== 'freediving-levels') {
              setShowLoginModal(true);
              return;
            }
            setActiveTab(value);
          }} className="w-full">
            <TabsList className="bg-transparent border-none h-auto p-0 space-x-8">
              <TabsTrigger value="dashboard" className="bg-transparent border-none text-blue-200 hover:text-white data-[state=active]:text-teal-300 data-[state=active]:border-b-2 data-[state=active]:border-teal-400 data-[state=active]:bg-transparent rounded-none pb-4 pt-4">
                <Trophy className="mr-2" size={18} />
                {t('dashboard')}
              </TabsTrigger>
              
              {/* Show other menus only when authenticated */}
              {isAuthenticated && (
                <>
                  {/* Contests Dropdown Menu */}
                  {hasAnyModuleInGroup(['competitions', 'registrations', 'live-events', 'event-results']) && (
                  <div className="relative inline-block" style={{ zIndex: 9999 }}>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowContestsDropdown(!showContestsDropdown);
                        setShowCareerDropdown(false);
                        setShowTrainingDropdown(false);
                        setShowManagementDropdown(false);
                      }}
                      className={`flex items-center bg-transparent border-none pb-4 pt-4 rounded-none ${
                        ['competitions', 'registrations', 'live-events', 'event-results'].includes(activeTab)
                          ? 'text-teal-300 border-b-2 border-teal-400'
                          : 'text-blue-200 hover:text-white'
                      }`}
                    >
                      <Calendar className="mr-2" size={18} />
                      {t('contests')}
                      <ChevronDown className={`ml-1 transition-transform ${showContestsDropdown ? 'rotate-180' : ''}`} size={16} />
                    </button>
                    
                    {showContestsDropdown && (
                      <>
                        {/* Click-away overlay */}
                        <div 
                          className="fixed inset-0" 
                          style={{ zIndex: 9998 }}
                          onClick={() => setShowContestsDropdown(false)}
                        />
                        
                        {/* Dropdown menu */}
                        <div 
                          className="absolute top-full left-0 mt-2 w-48 bg-blue-900 backdrop-blur-sm border border-blue-700/50 rounded-lg shadow-2xl"
                          style={{ zIndex: 9999, position: 'absolute' }}
                        >
                          {hasModule('competitions') && (
                          <button
                            onClick={(e) => { 
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('competitions'); 
                              setShowContestsDropdown(false); 
                            }}
                            className="w-full text-left px-4 py-3 text-blue-200 hover:bg-blue-800 hover:text-white flex items-center rounded-t-lg transition-colors"
                          >
                            <Calendar className="mr-2" size={16} />
                            {t('competitions')}
                          </button>
                          )}
                          {hasModule('registrations') && (
                          <button
                            onClick={(e) => { 
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('registrations'); 
                              setShowContestsDropdown(false); 
                            }}
                            className="w-full text-left px-4 py-3 text-blue-200 hover:bg-blue-800 hover:text-white flex items-center transition-colors"
                          >
                            <Settings className="mr-2" size={16} />
                            {t('registrations')}
                          </button>
                          )}
                          {hasModule('live-events') && (
                          <button
                            onClick={(e) => { 
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('live-events'); 
                              setShowContestsDropdown(false); 
                            }}
                            className="w-full text-left px-4 py-3 text-blue-200 hover:bg-blue-800 hover:text-white flex items-center transition-colors"
                          >
                            <Timer className="mr-2" size={16} />
                            {t('liveEvents')}
                          </button>
                          )}
                          {hasModule('event-results') && (
                          <button
                            onClick={(e) => { 
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('event-results'); 
                              setShowContestsDropdown(false); 
                            }}
                            className="w-full text-left px-4 py-3 text-blue-200 hover:bg-blue-800 hover:text-white flex items-center border-t border-blue-700/30 rounded-b-lg transition-colors"
                          >
                            <Trophy className="mr-2" size={16} />
                            {t('eventResults')}
                          </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  )}

                  {/* Career Dropdown Menu */}
                  {hasAnyModuleInGroup(['freedivers', 'follow-up', 'performance-comparison']) && (
                  <div className="relative inline-block" style={{ zIndex: 9999 }}>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowCareerDropdown(!showCareerDropdown);
                        setShowContestsDropdown(false);
                        setShowTrainingDropdown(false);
                        setShowManagementDropdown(false);
                      }}
                      className={`flex items-center bg-transparent border-none pb-4 pt-4 rounded-none ${
                        ['freedivers', 'follow-up', 'performance-comparison'].includes(activeTab)
                          ? 'text-teal-300 border-b-2 border-teal-400'
                          : 'text-blue-200 hover:text-white'
                      }`}
                    >
                      <Users className="mr-2" size={18} />
                      {t('career')}
                      <ChevronDown className={`ml-1 transition-transform ${showCareerDropdown ? 'rotate-180' : ''}`} size={16} />
                    </button>
                    
                    {showCareerDropdown && (
                      <>
                        {/* Click-away overlay */}
                        <div 
                          className="fixed inset-0" 
                          style={{ zIndex: 9998 }}
                          onClick={() => setShowCareerDropdown(false)}
                        />
                        
                        {/* Dropdown menu */}
                        <div 
                          className="absolute top-full left-0 mt-2 w-56 bg-blue-900 backdrop-blur-sm border border-blue-700/50 rounded-lg shadow-2xl"
                          style={{ zIndex: 9999, position: 'absolute' }}
                        >
                          {hasModule('freedivers') && (
                          <button
                            onClick={(e) => { 
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('freedivers'); 
                              setShowCareerDropdown(false); 
                            }}
                            className="w-full text-left px-4 py-3 text-blue-200 hover:bg-blue-800 hover:text-white flex items-center rounded-t-lg transition-colors"
                          >
                            <Users className="mr-2" size={16} />
                            {t('freedivers')}
                          </button>
                          )}
                          {hasModule('follow-up') && (
                          <button
                            onClick={(e) => { 
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('follow-up'); 
                              setShowCareerDropdown(false); 
                            }}
                            className="w-full text-left px-4 py-3 text-blue-200 hover:bg-blue-800 hover:text-white flex items-center transition-colors"
                          >
                            <Trophy className="mr-2" size={16} />
                            {t('followUp')}
                          </button>
                          )}
                          {hasModule('performance-comparison') && (
                          <button
                            onClick={(e) => { 
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('performance-comparison'); 
                              setShowCareerDropdown(false); 
                            }}
                            className="w-full text-left px-4 py-3 text-blue-200 hover:bg-blue-800 hover:text-white flex items-center rounded-b-lg transition-colors"
                          >
                            <BarChart3 className="mr-2" size={16} />
                            {t('performanceComparison')}
                          </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  )}
                  
                  {/* Training Module Dropdown */}
                  {hasAnyModuleInGroup(['training-sessions', 'exercise-library', 'session-builder', 'apnea-tables']) && (
                  <div className="relative inline-block" style={{ zIndex: 9999 }}>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowTrainingDropdown(!showTrainingDropdown);
                        setShowContestsDropdown(false);
                        setShowCareerDropdown(false);
                        setShowManagementDropdown(false);
                      }}
                      className={`flex items-center bg-transparent border-none pb-4 pt-4 rounded-none ${
                        ['training-sessions', 'exercise-library', 'session-builder', 'apnea-tables'].includes(activeTab)
                          ? 'text-teal-300 border-b-2 border-teal-400'
                          : 'text-blue-200 hover:text-white'
                      }`}
                    >
                      <Waves className="mr-2" size={18} />
                      {t('trainingModule') || 'Training Module'}
                      <ChevronDown className={`ml-1 transition-transform ${showTrainingDropdown ? 'rotate-180' : ''}`} size={16} />
                    </button>

                    {showTrainingDropdown && (
                      <>
                        <div
                          className="fixed inset-0"
                          style={{ zIndex: 9998 }}
                          onClick={() => setShowTrainingDropdown(false)}
                        />
                        <div
                          className="absolute top-full left-0 mt-2 w-52 bg-blue-900 backdrop-blur-sm border border-blue-700/50 rounded-lg shadow-2xl"
                          style={{ zIndex: 9999, position: 'absolute' }}
                        >
                          {hasModule('training-sessions') && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('training-sessions');
                              setShowTrainingDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-blue-800 hover:text-white flex items-center rounded-t-lg transition-colors ${activeTab === 'training-sessions' ? 'text-teal-300 bg-blue-800/40' : 'text-blue-200'}`}
                          >
                            <Waves className="mr-2" size={16} />
                            {t('trainingSessions')}
                          </button>
                          )}
                          {hasModule('exercise-library') && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('exercise-library');
                              setShowTrainingDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-blue-800 hover:text-white flex items-center transition-colors ${activeTab === 'exercise-library' ? 'text-teal-300 bg-blue-800/40' : 'text-blue-200'}`}
                          >
                            <BookOpen className="mr-2" size={16} />
                            {t('exerciseLibrary')}
                          </button>
                          )}
                          {hasModule('session-builder') && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('session-builder');
                              setShowTrainingDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-blue-800 hover:text-white flex items-center transition-colors ${activeTab === 'session-builder' ? 'text-teal-300 bg-blue-800/40' : 'text-blue-200'}`}
                          >
                            <Layers className="mr-2" size={16} />
                            {t('sessionBuilder')}
                          </button>
                          )}
                          {hasModule('apnea-tables') && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('apnea-tables');
                              setShowTrainingDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-blue-800 hover:text-white flex items-center rounded-b-lg transition-colors ${activeTab === 'apnea-tables' ? 'text-teal-300 bg-blue-800/40' : 'text-blue-200'}`}
                            data-testid="nav-apnea-tables"
                          >
                            <Waves className="mr-2" size={16} />
                            Tables d'Apnée
                          </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  )}
                  
                  <TabsTrigger value="aquatic-infrastructure" className="hidden">infrastructure</TabsTrigger>
                  <TabsTrigger value="organizations" className="hidden">organizations</TabsTrigger>
                  
                  {/* Management Dropdown (Infrastructure + Organizations) */}
                  {hasAnyModuleInGroup(['organizations', 'aquatic-infrastructure']) && (
                  <div className="relative inline-block" style={{ zIndex: 9999 }}>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowManagementDropdown(!showManagementDropdown);
                        setShowContestsDropdown(false);
                        setShowCareerDropdown(false);
                        setShowTrainingDropdown(false);
                        setShowLevelsDropdown(false);
                      }}
                      className={`flex items-center bg-transparent border-none pb-4 pt-4 rounded-none ${
                        ['aquatic-infrastructure', 'organizations'].includes(activeTab)
                          ? 'text-teal-300 border-b-2 border-teal-400'
                          : 'text-blue-200 hover:text-white'
                      }`}
                    >
                      <Briefcase className="mr-2" size={18} />
                      {t('management') || 'Management'}
                      <ChevronDown className={`ml-1 transition-transform ${showManagementDropdown ? 'rotate-180' : ''}`} size={16} />
                    </button>

                    {showManagementDropdown && (
                      <>
                        <div
                          className="fixed inset-0"
                          style={{ zIndex: 9998 }}
                          onClick={() => setShowManagementDropdown(false)}
                        />
                        <div
                          className="absolute top-full left-0 mt-2 w-56 bg-blue-900 backdrop-blur-sm border border-blue-700/50 rounded-lg shadow-2xl"
                          style={{ zIndex: 9999, position: 'absolute' }}
                        >
                          {hasModule('organizations') && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('organizations');
                              setShowManagementDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-blue-800 hover:text-white flex items-center rounded-t-lg transition-colors ${activeTab === 'organizations' ? 'text-teal-300 bg-blue-800/40' : 'text-blue-200'}`}
                          >
                            <Building2 className="mr-2" size={16} />
                            {t('organizations') || 'Organization(s)'}
                          </button>
                          )}
                          {hasModule('aquatic-infrastructure') && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setActiveTab('aquatic-infrastructure');
                              setShowManagementDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-3 hover:bg-blue-800 hover:text-white flex items-center rounded-b-lg transition-colors ${activeTab === 'aquatic-infrastructure' ? 'text-teal-300 bg-blue-800/40' : 'text-blue-200'}`}
                          >
                            <Anchor className="mr-2" size={16} />
                            {t('aquaticInfrastructure')}
                          </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  )}
                </>
              )}
              
              {/* Freediving Levels Dropdown - Always visible (no auth required) */}
              <div className="relative inline-block" style={{ zIndex: 9999 }}>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowLevelsDropdown(!showLevelsDropdown);
                    setShowContestsDropdown(false);
                    setShowCareerDropdown(false);
                    setShowTrainingDropdown(false);
                    setShowManagementDropdown(false);
                  }}
                  className={`flex items-center bg-transparent border-none pb-4 pt-4 rounded-none ${
                    activeTab === 'freediving-levels'
                      ? 'text-teal-300 border-b-2 border-teal-400'
                      : 'text-blue-200 hover:text-white'
                  }`}
                >
                  <Award className="mr-2" size={18} />
                  {t('freedivingLevels')}
                  <ChevronDown className={`ml-1 transition-transform ${showLevelsDropdown ? 'rotate-180' : ''}`} size={16} />
                </button>

                {showLevelsDropdown && (
                  <>
                    <div
                      className="fixed inset-0"
                      style={{ zIndex: 9998 }}
                      onClick={() => setShowLevelsDropdown(false)}
                    />
                    <div
                      className="absolute top-full left-0 mt-2 w-56 bg-blue-900 backdrop-blur-sm border border-blue-700/50 rounded-lg shadow-2xl max-h-96 overflow-y-auto"
                      style={{ zIndex: 9999, position: 'absolute' }}
                    >
                      {/* All Agencies option */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedLevelAgency(null);
                          setActiveTab('freediving-levels');
                          setShowLevelsDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-blue-800 hover:text-white flex items-center rounded-t-lg transition-colors border-b border-blue-700/30 ${activeTab === 'freediving-levels' && !selectedLevelAgency ? 'text-teal-300 bg-blue-800/40' : 'text-blue-200'}`}
                      >
                        <Award className="mr-2" size={16} />
                        {language === 'fr' ? 'Tous les organismes' : 'All Agencies'}
                      </button>
                      {/* Equivalence Chart option - second position */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedLevelAgency('equivalence');
                          setActiveTab('freediving-levels');
                          setShowLevelsDropdown(false);
                        }}
                        className={`w-full text-left px-4 py-3 hover:bg-blue-800 hover:text-white flex items-center transition-colors border-b border-blue-700/30 ${activeTab === 'freediving-levels' && selectedLevelAgency === 'equivalence' ? 'text-teal-300 bg-blue-800/40' : 'text-blue-200'}`}
                      >
                        <BarChart3 className="mr-2" size={14} />
                        {language === 'fr' ? 'Tableau d\'equivalence' : 'Equivalence Chart'}
                      </button>
                      {/* Individual agencies */}
                      {['CMAS', 'AIDA', 'Molchanovs', 'ApneaAcademy', 'PADI', 'SSI', 'FFESSM', 'Other'].map((agency, idx) => (
                        <button
                          key={agency}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedLevelAgency(agency);
                            setActiveTab('freediving-levels');
                            setShowLevelsDropdown(false);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-blue-800 hover:text-white flex items-center transition-colors ${idx === 7 ? 'rounded-b-lg' : ''} ${activeTab === 'freediving-levels' && selectedLevelAgency === agency ? 'text-teal-300 bg-blue-800/40' : 'text-blue-200'}`}
                        >
                          <ChevronRight className="mr-2" size={14} />
                          {agency === 'ApneaAcademy' ? 'Apnea Academy' : agency}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
              
            </TabsList>
          </Tabs>
        </div>
      </nav>

      {/* Active Live Event Banner */}
      {activeLiveSession && (
        <div className="relative bg-gradient-to-r from-green-900/90 to-teal-900/90 border-b border-green-700/50 backdrop-blur-sm" style={{ zIndex: 999 }} data-testid="active-live-banner">
          <div className="container mx-auto px-6 py-2.5">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <Badge className="bg-green-600 animate-pulse text-white">
                  <Play size={10} className="mr-1" />
                  LIVE
                </Badge>
                <div>
                  <span className="text-white font-semibold text-sm">{activeLiveSession.competition_name}</span>
                  <span className="text-green-300 mx-2">&bull;</span>
                  <span className="text-green-200 text-sm">{activeLiveSession.name}</span>
                  {activeLiveSession.event_type_code && (
                    <span className="text-teal-300 text-xs ml-2">({activeLiveSession.event_type_code})</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs text-green-200">
                {activeLiveSession.location && (
                  <span className="flex items-center gap-1">
                    <MapPin size={12} className="text-teal-400" />
                    {activeLiveSession.location}
                  </span>
                )}
                {activeLiveSession.pool_type && (
                  <span className="flex items-center gap-1">
                    <Layers size={12} className="text-teal-400" />
                    {activeLiveSession.pool_type}
                    {activeLiveSession.pool_length ? ` ${activeLiveSession.pool_length}m` : ''}
                    {activeLiveSession.lanes ? ` - ${activeLiveSession.lanes} ${t('lanes') || 'lanes'}` : ''}
                  </span>
                )}
                {activeLiveSession.actual_start_time && (
                  <span className="flex items-center gap-1 font-mono">
                    <Timer size={12} className="text-teal-400" />
                    {new Date(activeLiveSession.actual_start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </span>
                )}
                <Button
                  onClick={() => { 
                    setActiveTab('live-events'); 
                    setShowContestsDropdown(false);
                    setShowCareerDropdown(false);
                    setShowTrainingDropdown(false);
                    window.scrollTo(0, 0);
                  }}
                  size="sm"
                  className="bg-green-700/50 hover:bg-green-600/50 text-white text-xs h-7 px-3"
                  data-testid="go-to-live-event-btn"
                >
                  <Eye size={12} className="mr-1" />
                  {t('view') || 'View'}
                </Button>
                <Button
                  onClick={() => window.open(`/spectator/${activeLiveSession.id}`, '_blank')}
                  size="sm"
                  className="bg-cyan-700/50 hover:bg-cyan-600/50 text-white text-xs h-7 px-3"
                  data-testid="banner-spectator-btn"
                >
                  <ExternalLink size={12} className="mr-1" />
                  {t('spectator') || 'Spectator'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="relative container mx-auto px-6 py-8" style={{ zIndex: 1 }}>
        <Tabs value={activeTab}>
          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <DashboardTab stats={stats} eventTypes={eventTypes} t={t} freedivers={freedivers} trainingSessions={trainingSessions} language={language} activeLiveSession={activeLiveSession} onGoToLiveEvent={() => setActiveTab('live-events')} eventSessions={eventSessions} />
          </TabsContent>

          {/* Competitions Tab */}
          <TabsContent value="competitions">
            <CompetitionsTab 
              t={t} 
              language={language}
              competitions={competitions}
              aquaticInfrastructures={aquaticInfrastructures}
              users={allUsers}
              onRefresh={fetchData}
            />
          </TabsContent>

          {/* Freedivers Tab */}
          <TabsContent value="freedivers">
            <FreediversTab 
              t={t}
              language={language}
              freedivers={freedivers}
              onRefresh={fetchData}
              onViewPerformances={(freediver) => {
                setSelectedFreediverForFollowUp(freediver);
                fetchFreediverPerformances(freediver.id);
                setActiveTab('follow-up');
              }}
            />
          </TabsContent>

          {/* Registrations Tab */}
          <TabsContent value="registrations">
            <RegistrationsTab
              competitions={competitions}
              freedivers={freedivers}
              eventTypes={eventTypes}
              groupedRegistrations={visibleGroupedRegistrations}
              onCreateEventSession={createEventSession}
              onRefresh={() => { fetchData(); fetchRegistrations(); }}
              t={t}
            />
          </TabsContent>

          {/* Training Sessions Tab */}
          <TabsContent value="training-sessions">
            <TrainingSessionsTab
              trainingSessions={trainingSessions}
              trainingTemplates={trainingTemplates}
              customTemplates={customTemplates}
              coaches={coaches}
              freedivers={freedivers}
              aquaticInfrastructures={aquaticInfrastructures}
              onRefresh={() => { fetchTrainingSessions(); fetchCustomTemplates(); }}
              t={t}
              language={language}
            />
          </TabsContent>

          {/* Apnea Tables Tab */}
          <TabsContent value="apnea-tables">
            <ApneaTablesTab t={t} language={language} />
          </TabsContent>

          {/* Live Events Tab */}
          <TabsContent value="live-events">
            <LiveEventsTab 
              t={t}
              language={language}
              currentUser={currentUser}
              competitions={competitions}
              eventTypes={eventTypes}
              aquaticInfrastructures={aquaticInfrastructures}
              eventSessions={eventSessions}
              currentSession={currentSession}
              currentStatus={currentStatus}
              eventStartTime={eventStartTime}
              eventElapsedTime={eventElapsedTime}
              countdownToNext={countdownToNext}
              countdownActive={countdownActive}
              countdownPaused={countdownPaused}
              runningOrder={runningOrder}
              showRunningOrder={showRunningOrder}
              onRefresh={() => { fetchEventSessions(); fetchActiveLiveSession(); }}
              onStartSession={startEventSession}
              onResetSession={restartEventSession}
              onStopSession={stopEventSession}
              onResetEventTimer={() => { setEventStartTime(new Date()); setEventElapsedTime(0); }}
              onToggleCountdown={() => setCountdownActive(!countdownActive)}
              onPauseCountdown={() => setCountdownPaused(!countdownPaused)}
              onCompletePassage={handlePassageComplete}
              onCardPassage={handlePassageCard}
              onShowRunningOrder={(session) => {
                fetchRunningOrder(session.id);
                setShowRunningOrder(true);
              }}
              onCloseRunningOrder={() => setShowRunningOrder(false)}
              onGenerateResults={generateEventResults}
              onClearOrphans={clearOrphanSessions}
              onForceStart={forceStartEventNow}
              onSavePerformance={savePerformanceWithPenalties}
              simulationMode={settingsForm.event_live_simulation}
              onSkipToNext={skipToNextCountdown}
              onResetToPending={resetActiveSessionToPending}
              eventSettings={settingsForm}
              onRefreshStatus={async () => {
                if (currentSession) {
                  try {
                    const statusRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${currentSession}/current-status`);
                    setCurrentStatus(statusRes.data);
                    // Also refresh running order
                    const roRes = await axios.get(`${API_BASE_URL}/api/event-sessions/${currentSession}/running-order`);
                    setRunningOrder(roRes.data);
                    // Update countdown from server
                    if (statusRes.data.seconds_to_official_top > 0) {
                      setCountdownToNext(statusRes.data.seconds_to_official_top);
                      setCountdownActive(true);
                    } else {
                      setCountdownToNext(0);
                      setCountdownActive(false);
                    }
                  } catch (e) {
                    console.log('Refresh status error:', e);
                  }
                }
              }}
            />
          </TabsContent>

          {/* Event Results Tab */}
          <TabsContent value="event-results">
            <EventResultsTab
              competitions={competitions}
              eventSessions={eventSessions}
              selectedCompetition={selectedCompetition}
              onSelectCompetition={(comp) => {
                setSelectedCompetition(comp);
                fetchEventSessionsForResults(comp.id);
              }}
              onFetchRunningOrder={generateEventResults}
              t={t}
              language={language}
            />
          </TabsContent>

          {/* Follow Up Tab */}
          <TabsContent value="follow-up">
            <FollowUpTab
              freedivers={freedivers}
              coaches={coaches}
              selectedFreediver={selectedFreediverForFollowUp}
              freediverPerformances={freediverPerformances}
              onSelectFreediver={fetchFreediverPerformances}
              onCreatePerformance={handleCreatePerformanceWrapper}
              onUpdatePerformance={handleUpdatePerformanceWrapper}
              onDeletePerformance={handleDeletePerformanceWrapper}
              t={t}
            />
          </TabsContent>

          {/* Performance Comparison Tab */}
          <TabsContent value="performance-comparison">
            <PerformanceComparisonTab
              freedivers={freedivers}
              allPerformances={performances}
              translations={translations[language]}
              t={t}
            />
          </TabsContent>

          {/* Aquatic Infrastructure Tab */}
          <TabsContent value="aquatic-infrastructure">
            <AquaticInfrastructureTab
              aquaticInfrastructures={aquaticInfrastructures}
              onCreateInfrastructure={handleCreateInfrastructureWrapper}
              onUpdateInfrastructure={handleUpdateInfrastructureWrapper}
              onDeleteInfrastructure={handleDeleteInfrastructureWrapper}
            />
          </TabsContent>

          {/* Exercise Library Tab */}
          <TabsContent value="exercise-library">
            <ExerciseLibraryTab 
              t={t}
              currentUser={currentUser}
            />
          </TabsContent>

          {/* Session Builder Tab */}
          <TabsContent value="session-builder">
            <SessionBuilderTab 
              t={t}
              currentUser={currentUser}
              onSaveSession={() => fetchTrainingSessions()}
            />
          </TabsContent>

          <TabsContent value="freediving-levels">
            <FreedivingLevelsTab 
              t={t}
              language={language}
              selectedAgency={selectedLevelAgency}
            />
          </TabsContent>

          <TabsContent value="organizations">
            <OrganizationsTab
              t={t}
              language={language}
              freedivers={freedivers}
              currentUser={currentUser}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="bg-gradient-to-br from-blue-900 to-teal-900 border-blue-700 text-white max-w-md w-[92vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-blue-200 flex items-center">
              <LogIn size={24} className="mr-2 text-teal-400" />
              {t('signInTitle')}
            </DialogTitle>
            <DialogDescription className="text-blue-300">
              {t('signInDescription')}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLogin} className="space-y-4">
            {loginError && (
              <Alert className="bg-red-900/50 border-red-700 text-red-200">
                <AlertDescription>{loginError}</AlertDescription>
              </Alert>
            )}
            <div>
              <Label className="text-blue-200">{t('username')}</Label>
              <Input
                value={loginForm.username}
                onChange={(e) => setLoginForm({...loginForm, username: e.target.value})}
                className="bg-blue-800/50 border-blue-600 text-white"
                placeholder={t('enterUsername')}
                required
              />
            </div>
            <div>
              <Label className="text-blue-200">{t('password')}</Label>
              <Input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({...loginForm, password: e.target.value})}
                className="bg-blue-800/50 border-blue-600 text-white"
                placeholder={t('enterPassword')}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowLoginModal(false)} className="border-blue-500 text-blue-200">
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-teal-600 hover:bg-teal-700">
                <LogIn size={16} className="mr-2" />
                {t('signIn')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* User Management Modal (extracted component) */}
      <UserManagementModal
        open={showUserManagement}
        onOpenChange={setShowUserManagement}
        authToken={authToken}
        t={t}
        organizations={organizations}
      />
      {/* Global Settings Modal */}
      <GlobalSettingsDialog
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        settings={globalSettings}
        onSaved={(updated) => {
          setGlobalSettings(updated);
          if (fetchSettings) fetchSettings();
        }}
        t={t}
      />

      {/* Event Results Modal */}
      <EventResultsModal
        show={showResults}
        onClose={() => setShowResults(false)}
        resultsData={eventResults}
        t={t}
        language={language}
        onFilterGender={(gender) => {
          if (eventResults?.session?.id) {
            generateEventResults(eventResults.session.id, gender);
          }
        }}
      />
    </div>
  );
}

export default App;